// PublicBooking — patient-facing booking widget served at book.doctrue.in.
//
// Visual language mirrors the admin AppointmentBooking screen so that
// hospitals see a consistent product, but the flow is meaningfully
// different:
//   - Unauthenticated. There's no userData / hospital context — only the
//     hospital_id parsed from the URL drives every API call.
//   - OTP-gated. A WhatsApp OTP step sits between "Book Appointment" and
//     the actual /patient + /booking calls when the hospital opts in via
//     the public_booking_otp_required flag (default true / fail-secure).
//   - No walk-ins. Only "booking" mode (no walk-in / video) — see notes
//     on the BookingType type below.
//
// Key memories used here (see ~/.claude memory directory):
//   - project_dow_numbering        (day_of_week is Sun=1..Sat=7)
//   - project_express_validator_optional (omit null queue-type fields)
//   - project_public_booking_otp_flag    (OTP feature flag semantics)
//   - project_public_booking_auth_redirect (UserContext guard for /?public=1)
//   - project_public_booking_device_trust (10-day OTP-skip cookie window)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { FiUser, FiSearch, FiChevronRight, FiX, FiCheck, FiInfo, FiCreditCard, FiCalendar, FiClock, FiPhone, FiMessageSquare, FiLayers } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import PhoneNumberSelect from "../components/atoms/PhoneNumberSelect";
import PublicHimsLookup from "../components/molecules/PublicHimsLookup";
import {
  allowedCountryCodes,
  isValidNationalLength,
  maxNationalLength,
  nationalLengths,
  splitPhone,
  cleanNationalInput,
} from "../lib/utils/countryCodes";
import { publicHref } from "../lib/utils/publicHost";
import {
  getPublicHospital,
  getPublicFeatureFlags,
  getPublicOnlineDoctors,
  getPublicDoctors,
  getPublicDoctorAvailableDates,
  getPublicTimeBlocks,
  PublicTimeBlock,
  getPublicDoctorAvailability,
  getPublicSlots,
  getPublicTokens,
  searchPublicPatient,
  createPublicPatient,
  createPublicBooking,
  sendBookingOtp,
  verifyBookingOtp,
  exchangeLinkSession,
  getPublicHousehold,
  addPublicHouseholdMember,
  updatePublicPatient,
  linkPublicHimsPatient,
  getPublicPrograms,
  setPublicBookingPrograms,
  Household,
  HouseholdPerson,
  PublicHimsMatched,
  PublicHimsPatient,
  PublicProgram,
  RELATION_TYPES,
  RelationType,
  HospitalBranding,
  PublicFeatureFlags,
  PublicDoctor,
  PublicDoctorAvailability,
  PublicSlot,
  PublicToken,
  getPublicBookingDetail,
  reschedulePublicBooking,
  bookingPageTokenKey,
  PublicBookingDetail,
  ReschedulePayload,
  getPublicAvailabilityAdjustment,
  PublicAvailabilityAdjustment,
} from "../lib/apis/publicBooking";

const todayStr = () => moment().format("YYYY-MM-DD");

type Gender = "Male" | "Female";
// PublicBooking only supports physical online bookings — walk-ins happen
// at the front desk via the admin AppointmentBooking flow, not from the
// patient-facing widget. Visit type is the patient's only mode toggle.
type VisitType = "New" | "Follow Up";

// Phones get the one-step-at-a-time flow; md+ (Tailwind's 768px) keeps the
// two-column card. Listens for resize/rotation so the layout follows.
// What the confirmation screen shows, captured the moment a booking succeeds.
interface ConfirmedBooking {
  ref: string;
  patient: string;
  phone: string;
  doctor: string;
  when: string;
  time: string;
  visit: string;
  // The doctor takes a fee before the visit: the booking is held until paid.
  payment?: { amount: number | null; currency?: string; link: string | null; expires_at?: string | null } | null;
  // A booking-page token came back, so the screen can open the booking's own page.
  manageable?: boolean;
  // Shown after a reschedule instead of a new booking.
  rescheduled?: boolean;
}

// Why the booking's manage page won't allow a reschedule (server reason codes).
const RESCHEDULE_BLOCKED_TEXT: Record<string, string> = {
  disabled: "This hospital doesn't allow rescheduling online. Please contact them.",
  status: "This appointment can no longer be changed online. Please contact the reception.",
  past: "This appointment has passed.",
  cutoff: "It's too close to your appointment to change it online. Please contact the hospital.",
  payment_pending: "Complete the payment first. An unpaid booking is released automatically.",
};

// Country codes the phone selector offers, longest first so "971…" is not
// read as "97" + rest. Used only to split a ?phone= prefill from WhatsApp.
// Age is typed as whole years, 0 (infant) to 150. Same bounds as the backend.
const MAX_AGE = 150;
const isValidAge = (v: string): boolean => /^\d{1,3}$/.test(v) && Number(v) <= MAX_AGE;
// The booking session (token + expiry + origin) for this tab. Kept in
// sessionStorage, not just memory, because the WhatsApp link token is
// single-use: once it has been exchanged a reload must not dead-end on "link
// already used". Tab-scoped, gone when the tab closes, dropped on any 401.
// `via: "whatsapp"` marks a session that came from the bot's card: those
// visitors skip OTP and get the family picker + hospital record; the server
// reads the same claim off the token to stamp wa_booking (nothing client-sent).
type StoredSession = { token: string; exp: number; via?: "whatsapp" };
const sessionStorageKey = (hospitalId: string) => `pb_session_${hospitalId}`;
const readStoredSession = (hospitalId: string): StoredSession | null => {
  try {
    const raw = window.sessionStorage.getItem(sessionStorageKey(hospitalId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed?.token || !parsed.exp || Date.now() > parsed.exp) return null;
    return parsed;
  } catch {
    return null;
  }
};
const writeStoredSession = (hospitalId: string, token: string, expiresInSeconds: number, via?: "whatsapp") => {
  try {
    window.sessionStorage.setItem(
      sessionStorageKey(hospitalId),
      JSON.stringify({ token, exp: Date.now() + Math.max(60, expiresInSeconds - 30) * 1000, ...(via ? { via } : {}) })
    );
  } catch {
    /* storage blocked */
  }
};
const clearStoredSession = (hospitalId: string | undefined) => {
  if (!hospitalId) return;
  try {
    window.sessionStorage.removeItem(sessionStorageKey(hospitalId));
  } catch {
    /* storage blocked */
  }
};

const MOBILE_QUERY = "(max-width: 767px)";
const useIsMobile = (): boolean => {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return mobile;
};

// The mobile flow. Date comes before doctor because the doctor list is
// fetched per date (only doctors online that day are offered), so picking
// the doctor first and the date later would silently reset the doctor.
// Display name for a doctor: "Dr. <name>" unless the doctor record has the
// prefix switched off (show_name_prefix = false), or the stored name already
// starts with it. Used everywhere a doctor is named, so all steps agree.
const doctorName = (d: PublicDoctor | null | undefined): string => {
  if (!d) return "";
  const name = String(d.full_name || "").trim();
  if (!name) return "";
  if (d.show_name_prefix === false || /^dr\.?\s/i.test(name)) return name;
  return `Dr. ${name}`;
};

// "HH:MM[:SS]" on a given date → moment. Used for the today-only cut-offs.
const atTime = (date: string, hm: string | undefined | null) =>
  moment(`${date} ${String(hm || "00:00").slice(0, 8)}`, "YYYY-MM-DD HH:mm:ss");
// wait_time arrives as minutes, "HH:MM:SS", or a pg interval object.
const waitMinutesOf = (w: PublicDoctorAvailability["wait_time"]): number | null => {
  if (w == null) return null;
  if (typeof w === "number") return w > 0 ? w : null;
  if (typeof w === "string") {
    const [h, m] = String(w).split(":").map(Number);
    const total = (h || 0) * 60 + (m || 0);
    return total > 0 ? total : null;
  }
  const total = (w.hours || 0) * 60 + (w.minutes || 0);
  return total > 0 ? total : null;
};

const MOBILE_STEPS = [
  { title: "Your details", subtitle: "Tell us who the appointment is for." },
  { title: "Date and doctor", subtitle: "Pick a date, then a doctor available that day." },
  { title: "Session and time", subtitle: "Choose the session and your slot." },
  { title: "Confirm booking", subtitle: "Check everything before you book." },
] as const;

const PublicBooking: React.FC = () => {
  const { hospital_id } = useParams<{ hospital_id: string }>();
  const isMobile = useIsMobile();
  // Current step of the mobile flow (0-based). Unused on md+.
  const [step, setStep] = useState(0);
  // Set when a booking is created; the form gives way to the confirmation screen.
  const [confirmed, setConfirmed] = useState<ConfirmedBooking | null>(null);

  // Reschedule mode: /<hospital>?reschedule=<booking_id>, opened from the
  // booking's manage page. The same doctor, a new date / session / time, all
  // through this page's own pickers; the patient step is skipped. Authorised by
  // the booking's page token, which the manage page left in this tab.
  const [rescheduleId] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get("reschedule") || null;
    } catch {
      return null;
    }
  });
  const rescheduling = !!rescheduleId;
  const [rescheduleToken] = useState<string | null>(() => {
    if (!rescheduleId) return null;
    try {
      return window.sessionStorage.getItem(bookingPageTokenKey(rescheduleId));
    } catch {
      return null;
    }
  });
  const [rescheduleOf, setRescheduleOf] = useState<PublicBookingDetail | null>(null);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  // Prefill from the WhatsApp link (?src=wa&phone=91XXXXXXXXXX&name=…&age=…&gender=…):
  // the bot knows who said hi and what their patient record holds, so nothing
  // already known is retyped. OTP still runs on this page, so a forwarded link
  // cannot book as someone else. src=wa marks the visit as WhatsApp-origin.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      // One-time link token from the bot (WhatsApp proved the number). It is
      // exchanged ONCE for a booking session, then removed from the address bar
      // so a reload, bookmark or share does not carry it. A used or expired
      // token shows the "link expired, send hi again" screen -- a WhatsApp
      // visitor is never pushed into OTP. `src=wa` by itself means nothing.
      const linkToken = (q.get("t") || "").trim();
      const fromWaLink = q.get("src") === "wa";
      if (linkToken && hospital_id) {
        q.delete("t");
        const clean = `${window.location.pathname}${q.toString() ? `?${q.toString()}` : ""}${window.location.hash}`;
        window.history.replaceState(null, "", clean);
        exchangeLinkSession(hospital_id, linkToken)
          .then((r) => {
            setBookingToken(r.booking_token);
            writeStoredSession(hospital_id, r.booking_token, r.expires_in_seconds, "whatsapp");
            setWaSession("active");
          })
          .catch(() => {
            const stored = readStoredSession(hospital_id);
            if (stored?.via === "whatsapp") {
              setBookingToken(stored.token);
              setWaSession("active");
            } else {
              setWaSession("expired");
            }
          });
      } else if (hospital_id) {
        const stored = readStoredSession(hospital_id);
        if (stored) {
          setBookingToken(stored.token);
          if (stored.via === "whatsapp") setWaSession("active");
        } else if (fromWaLink) {
          // Came in on a WhatsApp-shaped link with no usable token: the link
          // was consumed (or tampered with). Say so rather than ask for a code.
          setWaSession("expired");
        }
      }
      const ageParam = (q.get("age") || "").trim();
      if (isValidAge(ageParam)) setAge(String(Number(ageParam)));
      const genderParam = (q.get("gender") || "").trim().toLowerCase();
      if (genderParam === "male" || genderParam === "m") setGender("Male");
      else if (genderParam === "female" || genderParam === "f") setGender("Female");
      const digits = (q.get("phone") || "").replace(/\D/g, "");
      if (digits) {
        // Any country: the WhatsApp sender may be from a code the hospital
        // has not listed; their number is still theirs.
        const split = splitPhone(digits);
        if (split) {
          setCountryCode(split.code);
          setPhone(split.national);
        } else {
          setPhone(digits.slice(-10));
        }
      }
      const name = (q.get("name") || "").trim();
      if (name) setFullName(name);
    } catch {
      /* no window or a bad query string: nothing to prefill */
    }
  }, [hospital_id]);

  // ── Hospital context ─────────────────────────────────────────────
  // Branding + feature flags both come from public endpoints keyed by
  // hospital_id. We fetch them in parallel on mount.
  const [branding, setBranding] = useState<HospitalBranding | null>(null);
  const [flags, setFlags] = useState<PublicFeatureFlags | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);
  // Tripped when any public-route call returns 403 "Public booking is
  // currently disabled" mid-session (the hospital flipped the master
  // kill-switch after widget load). Renders the same unavailable screen
  // as the flag-on-load case.
  const [forcedDisabled, setForcedDisabled] = useState(false);

  // ── Patient form state ───────────────────────────────────────────
  // patientId is filled either by the pre-form lookup (OTP-disabled
  // hospitals) or inside runBookingFlow after verify (OTP-required).
  const [countryCode, setCountryCode] = useState("91");
  const [phone, setPhone] = useState("");
  // A new code can allow fewer digits: trim what is already typed.
  useEffect(() => {
    setPhone((p) => cleanNationalInput(countryCode, p));
  }, [countryCode]);
  const [fullName, setFullName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);
  // Language of this booking's WhatsApp messages (booking_lang): the patient's
  // chat language, passed by the bot as ?lang= (changed in WhatsApp with Change
  // Language). No picker on the page.
  const [messageLang, setMessageLang] = useState<string>(() => {
    try {
      const l = new URLSearchParams(window.location.search).get("lang") || "";
      return MESSAGE_LANG_NAMES[l] ? l : "en";
    } catch {
      return "en";
    }
  });
  const [patientId, setPatientId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // WhatsApp-origin session state. "active" = the card link was exchanged
  // for a session in this tab (no OTP, family picker + hospital record on);
  // "expired" = the link was already used / expired / tampered with.
  const [waSession, setWaSession] = useState<"none" | "active" | "expired">("none");

  // ── "Who is this appointment for?" (admin: Booking For + relations) ──
  // household = the phone's own patient row + the people it books for.
  // bookingFor = "self" | a relation's patient_id | "new" (add someone).
  const [household, setHousehold] = useState<Household | null>(null);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [bookingFor, setBookingFor] = useState<"self" | string>("self");
  // The "who is this for" popup (admin: the Manage Relation modal). Adding a
  // family member happens inside it, so the main form stays one line.
  const [householdOpen, setHouseholdOpen] = useState(false);
  // Hospital record (MRN) chosen from the hims panel; linked right before the
  // booking so the outbound push names the existing chart (admin: rememberMrn).
  const [pendingMrn, setPendingMrn] = useState<string | null>(null);
  const [himsResetToken, setHimsResetToken] = useState(0);
  // Appointment programs (optional chips), tagged after the booking exists.
  const [programOptions, setProgramOptions] = useState<PublicProgram[]>([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
  const [programsOpen, setProgramsOpen] = useState(false);
  // Why the OTP modal is open: "verify" = unlock family/records at step 1,
  // "book" = the final Book click. Both leave the same booking token behind.
  const [otpPurpose, setOtpPurpose] = useState<"verify" | "book">("book");

  // OTP gate — booking_token is short-lived (10 min). Held in-memory only:
  // backend explicitly forbade persistence to localStorage/sessionStorage/cookies.
  // Only used when the hospital's `public_booking_otp_required` flag is true
  // (the default — fail-secure).
  const [otpOpen, setOtpOpen] = useState(false);
  const [bookingToken, setBookingToken] = useState<string | null>(null);

  const [visitType, setVisitType] = useState<VisitType>("New");
  // PRN (patient record number) for Follow Up visits. Only offered when the
  // hospital tracks PRN-based revisits (prn_collection flag). Optional here;
  // sent as booking.prn_number, the same field the admin follow-up path uses.
  const [prn, setPrn] = useState("");
  // Free-form comments — optional. Mirrors admin AppointmentBooking's
  // textarea (formState.comments) and feeds the `comments` field of the
  // booking payload. Trim on submit but accept whitespace mid-edit so
  // the cursor doesn't fight the user.
  const [comments, setComments] = useState("");
  // Marketing-communications opt-in — defaults to FALSE per DPDP/GDPR
  // (pre-ticked boxes don't count as consent). NOT gating Submit, by
  // user pref — soft consent capture only. When true, runBookingFlow
  // includes `marketing_consent: true` on the patient create call and
  // backend stamps marketing_consent_at as the audit trail. Note: this
  // is opt-in for promotional WhatsApp/SMS only — it is NOT a Terms-of-
  // Service or Privacy-Policy acceptance (those would need a separate
  // backend column; see project_public_booking_marketing_consent memory).
  const [marketingConsent, setMarketingConsent] = useState(false);

  // ── Appointment selection state ──────────────────────────────────
  // mappingId is the (doctor × hospital) row id, NOT the doctor_id —
  // availability queries are keyed by mapping_id. The doctor_id used
  // in the final booking payload comes from the selected doctor row.
  const [appointmentDate, setAppointmentDate] = useState<string>(todayStr());
  const [doctors, setDoctors] = useState<PublicDoctor[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [mappingId, setMappingId] = useState<string | null>(null);
  const [doctorPickerOpen, setDoctorPickerOpen] = useState(false);
  const [doctorSearch, setDoctorSearch] = useState("");
  // Second way in on step 2: pick a specialization, then a doctor from it
  // (the bot's specialization flow). "" = all doctors.
  const [specialization, setSpecialization] = useState("");
  // The "i" on the chosen doctor's row: qualification, experience, fee.
  const [doctorInfoOpen, setDoctorInfoOpen] = useState(false);
  // The "By specialization" popup.
  const [specPickerOpen, setSpecPickerOpen] = useState(false);
  // Time-block booking: the session's blocks for the chosen date (hospital flag
  // + session/date not switched off). When active, the patient picks a block and
  // the earliest free slot / token inside it is booked for them; the WhatsApp
  // message shows the block (the server stamps time_block_start/end).
  const [timeBlocks, setTimeBlocks] = useState<{ active: boolean; blocks: PublicTimeBlock[] }>({ active: false, blocks: [] });
  const [selectedBlock, setSelectedBlock] = useState<PublicTimeBlock | null>(null);
  // Doctor-first flow: the chosen doctor's working dates in the booking window.
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [availableDatesLoading, setAvailableDatesLoading] = useState(false);
  // Re-evaluated every minute so sessions, slots and tokens that pass while
  // the page is open drop off (the admin screen is strict the same way).
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // The doctor's weekly sessions as stored; `availabilities` below is the
  // same list adjusted for the chosen date (extended / switched off that day).
  const [weeklyAvailabilities, setAvailabilities] = useState<PublicDoctorAvailability[]>([]);
  const [dayAdjustments, setDayAdjustments] = useState<{
    date: string;
    byId: Record<string, PublicAvailabilityAdjustment | null>;
  }>({ date: "", byId: {} });
  // Same rule as the staff booking screen: an Extension moves the session's
  // times for that date (more slots / tokens), an Unavailability hides it.
  const availabilities = useMemo(() => {
    if (dayAdjustments.date !== appointmentDate) return weeklyAvailabilities;
    return weeklyAvailabilities.flatMap((a) => {
      const adj = dayAdjustments.byId[a.availability_id];
      if (!adj) return [a];
      if (adj.type === "Unavailability") return [];
      if (adj.type === "Extension" && adj.extended_end_time) {
        const start = adj.session_start_time || a.session_start_time || a.start_time;
        return [{ ...a, start_time: start, session_start_time: start, end_time: adj.extended_end_time, session_end_time: adj.extended_end_time }];
      }
      return [a];
    });
  }, [weeklyAvailabilities, dayAdjustments, appointmentDate]);
  // Load that date's adjustment for each of the doctor's sessions on that weekday.
  useEffect(() => {
    const dow = appointmentDate ? moment(appointmentDate).day() + 1 : null;
    const onDay = weeklyAvailabilities.filter(
      (a) => a.is_active !== false && (a.day_of_week === dow || a.day_of_week == null)
    );
    if (!appointmentDate || onDay.length === 0) {
      setDayAdjustments({ date: appointmentDate, byId: {} });
      return;
    }
    let cancelled = false;
    Promise.all(
      onDay.map((a) =>
        getPublicAvailabilityAdjustment(a.availability_id, appointmentDate)
          .then((adj) => [a.availability_id, adj] as const)
          .catch(() => [a.availability_id, null] as const)
      )
    ).then((pairs) => {
      if (!cancelled) setDayAdjustments({ date: appointmentDate, byId: Object.fromEntries(pairs) });
    });
    return () => {
      cancelled = true;
    };
  }, [weeklyAvailabilities, appointmentDate]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState<
    string | null
  >(null);

  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [tokens, setTokens] = useState<PublicToken[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<PublicSlot | null>(null);
  const [selectedToken, setSelectedToken] = useState<PublicToken | null>(null);
  const [slotTokenLoading, setSlotTokenLoading] = useState(false);
  const [slotTokenKind, setSlotTokenKind] = useState<
    "slots" | "tokens" | "session" | null
  >(null);

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.mapping_id === mappingId) || null,
    [doctors, mappingId]
  );

  // OTP gate is per-hospital. Default true (fail-secure) — only an explicit
  // false from the flags response disables the OTP flow. When disabled, the
  // widget runs the old Phase-1 behavior: no OTP modal, no Bearer headers,
  // debounced pre-form patient search re-enabled.
  const otpRequired = flags?.public_booking_otp_required !== false;
  // Follow Up is opt-in per hospital — only show the toggle (and the PRN box)
  // when the hospital uses PRN-based follow-up tracking (matches admin).
  // Declared up here because the step validators reference it.
  const showFollowUp = flags?.prn_collection === true;
  // Admin booking-screen features, same hospital flags as AppointmentBooking.
  const himsLookupOn = flags?.hims_patient_lookup === true;
  const programsOn = flags?.appointment_program_enabled === true;
  // public_booking_flow "<first>_<second>": the date before or after the doctor,
  // and how the doctor is picked -- "doctor" = the list, "specialization" = the
  // By specialization popup (a specialization, then its doctors), "both" = both
  // boxes. Default date_both. Rescheduling keeps the doctor, so it always works
  // doctor-first (their dates).
  const bookingFlow = flags?.public_booking_flow || (flags?.wa_web_v2_doc_first === true ? "both_date" : "date_both");
  const [flowFirst, flowSecond] = bookingFlow.split("_");
  const docFirst = flowSecond === "date" || rescheduling;
  const doctorPicker = (flowFirst === "date" ? flowSecond : flowFirst) as "doctor" | "specialization" | "both";

  // Phone codes: the hospital's list (sales dashboard). A WhatsApp visitor whose
  // own code is not on it keeps it, listed first -- the number is theirs.
  const hospitalCodes = allowedCountryCodes(flags?.phone_country_codes);
  const countryOptions = hospitalCodes.includes(countryCode) ? hospitalCodes : [countryCode, ...hospitalCodes];
  // Nothing typed yet and the starting "91" is not offered here: start on the
  // hospital's first code instead.
  useEffect(() => {
    if (!flags || phone) return;
    if (!hospitalCodes.includes(countryCode)) setCountryCode(hospitalCodes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags, hospitalCodes.join(",")]);

  // Master kill-switch. Renders the unavailable screen and blocks the form.
  // null/undefined on the flag is treated as "enabled" so widgets keep
  // working when the backend hasn't shipped the flag yet — only an explicit
  // false (or a 403 mid-session) trips it.
  const publicDisabled =
    flags?.public_booking_enabled === false || forcedDisabled;

  // Date picker upper bound: today + public_max_advance_days. null on the
  // flag means "no max — accept anything." Default behaviour (when no flag
  // is returned at all) is also no max; backend will still 400 anything
  // out of its own range and we surface that fallback toast.
  // Reschedule mode: the server's last allowed date for this booking instead
  // (N days after its original date, or the advance limit; null = no limit).
  const rescheduleLastDate = rescheduleOf?.manage?.reschedule_last_date || null;
  const maxAppointmentDate = useMemo(() => {
    if (rescheduling) return rescheduleLastDate || undefined;
    const n = flags?.public_max_advance_days;
    if (n == null) return undefined;
    return moment().add(n, "days").format("YYYY-MM-DD");
  }, [flags?.public_max_advance_days, rescheduling, rescheduleLastDate]);

  // Backend's day_of_week is 1-indexed starting Sunday (Sun=1..Sat=7),
  // not JS's 0-indexed Sun=0..Sat=6 — so add 1 to moment().day().
  const isTodaySelected = appointmentDate === todayStr();
  const sessionsForDateAll = useMemo(() => {
    if (!appointmentDate || availabilities.length === 0) return [];
    const dow = moment(appointmentDate).day() + 1;
    return availabilities.filter(
      (a) => a.is_active !== false && (a.day_of_week === dow || a.day_of_week == null)
    );
  }, [availabilities, appointmentDate]);
  // Strict like the admin screen: on today, a session whose end has passed is
  // not offered at all (admin blocks it with "This session has ended").
  const sessionsForDate = useMemo(() => {
    if (!isTodaySelected) return sessionsForDateAll;
    const now = moment(nowTick);
    return sessionsForDateAll.filter((a) => atTime(appointmentDate, a.session_end_time || a.end_time).isAfter(now));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsForDateAll, isTodaySelected, appointmentDate, nowTick]);
  const allSessionsEndedToday = isTodaySelected && sessionsForDateAll.length > 0 && sessionsForDate.length === 0;

  // Auto-pick the first session for the chosen date when sessions change.
  useEffect(() => {
    if (sessionsForDate.length === 0) {
      setSelectedAvailabilityId(null);
      return;
    }
    setSelectedAvailabilityId((prev) => {
      if (prev && sessionsForDate.some((s) => s.availability_id === prev)) {
        return prev;
      }
      return sessionsForDate[0].availability_id;
    });
  }, [sessionsForDate]);

  // On session change: branch on queue_type.
  // - Slot or Slot_Token  → POST /public/slots
  // - Token               → POST /public/tokens
  // - Session             → no call (the whole session is the booking unit)
  useEffect(() => {
    setSelectedSlot(null);
    setSelectedToken(null);
    setSelectedBlock(null);

    if (!mappingId || !selectedAvailabilityId || !appointmentDate) {
      setSlots([]);
      setTokens([]);
      setSlotTokenKind(null);
      setSlotTokenLoading(false);
      setTimeBlocks({ active: false, blocks: [] });
      return;
    }
    // Blocks for this session + date, only where the hospital has the feature.
    if (flags?.time_block_booking === true) {
      getPublicTimeBlocks({
        availability_id: selectedAvailabilityId,
        booked_date: appointmentDate,
        ...(rescheduleId ? { exclude_booking_id: rescheduleId } : {}),
      })
        .then((tb) => {
          const active = tb.active === true && tb.blocks.length > 0;
          setTimeBlocks({ active, blocks: tb.blocks || [] });
          // Session queue: the block is the booking's time, so the earliest one
          // still open is preselected (today: its end not yet passed). The
          // patient can pick a later one.
          const row = availabilities.find((a) => a.availability_id === selectedAvailabilityId);
          if (active && row?.queue_type === "Session") {
            const now = moment();
            const today = appointmentDate === todayStr();
            const first =
              (tb.blocks || []).find((b) => !b.full && (!today || atTime(appointmentDate, b.end).isAfter(now))) || null;
            setSelectedBlock(first);
          }
        })
        .catch(() => setTimeBlocks({ active: false, blocks: [] }));
    } else {
      setTimeBlocks({ active: false, blocks: [] });
    }
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    if (!session) return;

    if (session.queue_type === "Session") {
      setSlots([]);
      setTokens([]);
      setSlotTokenKind("session");
      // Nothing to load for a Session queue. Clear the flag here: a slot / token
      // request for the previous doctor may have been cancelled mid-flight, and
      // its finally() won't reset it -- the time-block chips would then sit on
      // "Loading..." forever.
      setSlotTokenLoading(false);
      return;
    }

    let cancelled = false;
    setSlotTokenLoading(true);
    setSlots([]);
    setTokens([]);

    // Public booking is always a "booking" (vs admin's "walk-in" / "video"):
    // the patient is reserving an upcoming slot online — no walk-in path here.
    const payload = {
      mapping_id: mappingId,
      availability_id: selectedAvailabilityId,
      booked_date: appointmentDate,
      start_time: session.start_time,
      end_time: session.end_time,
      number_of_people: 1,
      bookingType: "booking",
    } as const;

    const isToken = session.queue_type === "Token";
    const kind: "slots" | "tokens" = isToken ? "tokens" : "slots";
    setSlotTokenKind(kind);

    if (isToken) {
      getPublicTokens(payload)
        .then((list) => {
          if (cancelled) return;
          // Today: hide tokens whose turn has already passed. Token n is seen at
          // session start + n × wait time (admin hides it once that time is
          // behind "now"). Without a wait time every remaining token stays.
          const today = appointmentDate === todayStr();
          const wait = waitMinutesOf(session.wait_time);
          if (today && wait) {
            const start = atTime(appointmentDate, session.session_start_time || session.start_time);
            const now = moment();
            setTokens(list.filter((t) => start.clone().add(t.token_number * wait, "minutes").isAfter(now)));
          } else {
            setTokens(list);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          if (isKillSwitchError(err)) {
            setForcedDisabled(true);
            return;
          }
          setTokens([]);
        })
        .finally(() => {
          if (!cancelled) setSlotTokenLoading(false);
        });
    } else {
      getPublicSlots(payload)
        .then((list) => {
          if (cancelled) return;
          // Today: only slots that have not ended yet (admin: slot.end after now).
          const today = appointmentDate === todayStr();
          const now = moment();
          setSlots(today ? list.filter((sl) => atTime(appointmentDate, sl.end).isAfter(now)) : list);
        })
        .catch((err) => {
          if (cancelled) return;
          if (isKillSwitchError(err)) {
            setForcedDisabled(true);
            return;
          }
          setSlots([]);
        })
        .finally(() => {
          if (!cancelled) setSlotTokenLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    mappingId,
    selectedAvailabilityId,
    appointmentDate,
    availabilities,
    flags?.time_block_booking,
    rescheduleId,
  ]);

  const resetForm = () => {
    setPhone("");
    setFullName("");
    setAge("");
    setGender(null);
    setVisitType("New");
    setPrn("");
    setComments("");
    setMarketingConsent(false);
    setSelectedSlot(null);
    setSelectedToken(null);
    setPatientId(null);
    setBookingToken(null);
    setBookingFor("self");
    setPendingMrn(null);
    setHimsResetToken((t) => t + 1);
    setSelectedProgramIds([]);
    setStep(0);
    // Keep date + selected doctor — common reset behaviour in admin keeps
    // them so the user doesn't have to re-pick after a typo correction.
  };

  // Validation, split by step so the mobile flow can check one step at a
  // time with the same messages. validateForm runs all three (desktop Book).
  const validatePatientStep = (): boolean => {
    if (!hospital_id) return false;
    if (!fullName.trim()) {
      toast.error("Please enter the full name.");
      return false;
    }
    if (!phone.trim()) {
      toast.error("Please enter the phone number.");
      return false;
    }
    if (!isValidNationalLength(countryCode, phone)) {
      toast.error(`Phone number must be ${nationalLengths(countryCode).join(" or ")} digits for +${countryCode}.`);
      return false;
    }
    if (!isValidAge(age)) {
      toast.error(`Please enter a valid age (0 to ${MAX_AGE}).`);
      return false;
    }
    if (!gender) {
      toast.error("Please select a gender.");
      return false;
    }
    if (showFollowUp && visitType === "Follow Up" && prn.trim().length > 40) {
      toast.error("PRN number must be at most 40 characters.");
      return false;
    }
    return true;
  };

  const validateDoctorStep = (): boolean => {
    if (!selectedDoctor) {
      toast.error("Please choose a doctor.");
      return false;
    }
    if (docFirst) {
      if (availableDates.length > 0 && !availableDates.includes(appointmentDate)) {
        toast.error(`${doctorName(selectedDoctor)} is not available on this date. Please pick one of the dates shown.`);
        return false;
      }
      if (!availableDatesLoading && availableDates.length === 0) {
        toast.error(`${doctorName(selectedDoctor)} has no available dates in the booking window.`);
        return false;
      }
    } else if (selectedDoctor.available_on_date === false || selectedDoctor.bookable === false) {
      toast.error(`${doctorName(selectedDoctor)} is not available on this day. Please pick another doctor or date.`);
      return false;
    }
    return true;
  };

  const validateTimeStep = (): boolean => {
    if (!selectedAvailabilityId) {
      toast.error("Please pick a session.");
      return false;
    }
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    if (!session) {
      toast.error("Session not found, please re-pick.");
      return false;
    }
    if (appointmentDate === todayStr() && !atTime(appointmentDate, session.session_end_time || session.end_time).isAfter(moment())) {
      toast.error("This session has ended. Please select an active session.");
      return false;
    }
    if (blocksActive && !selectedBlock) {
      toast.error("Please choose a time.");
      return false;
    }
    if (
      (session.queue_type === "Slot" || session.queue_type === "Slot_Token") &&
      !selectedSlot
    ) {
      toast.error(blocksActive ? "No free slot in that time. Please choose another." : "Please choose a slot timing.");
      return false;
    }
    if (session.queue_type === "Token" && !selectedToken) {
      toast.error("Please choose a token.");
      return false;
    }
    return true;
  };

  const validateForm = (): boolean =>
    (rescheduling || validatePatientStep()) && validateDoctorStep() && validateTimeStep();

  // Click "Book Appointment" → validate. If the hospital requires OTP, open
  // the OTP modal (which then drives runBookingFlow on verify). If OTP is
  // disabled for this hospital, run the booking flow directly with no token.
  const handleSubmit = () => {
    if (!validateForm()) return;
    if (rescheduling) {
      runReschedule();
      return;
    }
    if (otpRequired && !bookingToken) {
      setOtpPurpose("book");
      setOtpOpen(true);
    } else {
      // Verified at step 1 (or OTP off): the token, if any, is still good.
      runBookingFlow(bookingToken);
    }
  };

  // Phase 2 + 3 from the canonical spec, run when the user clicks Book.
  //   1. searchPublicPatient — if hit, reuse patient_id; if miss/error, fall
  //      through to create. Both branches mirror the admin path.
  //   2. createPublicPatient — 200/201 are both treated as success per the
  //      backend's dedup contract (200 = existing row was found).
  //   3. createPublicBooking — the actual booking call.
  // On success (or 409 idempotency replay) we toast + resetForm; 401 sends
  // the user back through the OTP modal with the phone field preserved.
  // `token` is the booking_token JWT issued by /verify-otp, or null when
  // the hospital has OTP disabled — the helpers in publicBooking.ts omit
  // the Authorization header entirely when token is null.
  // Everything the confirmation screen shows, captured before resetForm wipes it.
  const snapshotBooking = (ref: string): ConfirmedBooking => {
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    const start = session ? session.session_start_time || session.start_time || "" : "";
    const end = session ? session.session_end_time || session.end_time || "" : "";
    const sessionText = start || end ? `${formatTime(start)} - ${formatTime(end)}` : "";
    const qt = session?.queue_type;
    // The one time the patient should plan around: their time block if the
    // session has blocks, the whole session for Session queues, else their
    // token or slot.
    const time = blocksActive && selectedBlock
      ? blockWithUnitLabel(selectedBlock)
      : qt === "Session"
        ? sessionText
        : qt === "Token" && selectedToken
          ? `Token ${selectedToken.token_number}`
          : (qt === "Slot" || qt === "Slot_Token") && selectedSlot
            ? `${formatTime(selectedSlot.start)} - ${formatTime(selectedSlot.end)}`
            : "";
    return {
      ref,
      patient: [fullName.trim(), relationLabel()].filter(Boolean).join(" · "),
      phone: formatIntl(confirmationPhone()),
      doctor: selectedDoctor
        ? `${doctorName(selectedDoctor)}${selectedDoctor.specialization ? ` · ${selectedDoctor.specialization}` : ""}`
        : "",
      when: [moment(appointmentDate).format("ddd, D MMM YYYY"), time].filter(Boolean).join(" · "),
      time,
      visit:
        showFollowUp && visitType === "Follow Up"
          ? `Follow Up${prn.trim() ? ` · PRN ${prn.trim()}` : ""}`
          : "",
    };
  };

  // Reschedule: move the booking to the picked session / slot / token. The
  // server re-checks everything (hospital setting, cutoff, the unit is free).
  const runReschedule = async () => {
    if (!hospital_id || !rescheduleId || !rescheduleToken || !selectedAvailabilityId) return;
    const session = availabilities.find((a) => a.availability_id === selectedAvailabilityId);
    if (!session) return;
    const qt = session.queue_type || "Slot";
    const payload: ReschedulePayload = { hospital_id, availability_id: selectedAvailabilityId, booked_date: appointmentDate };
    if ((qt === "Slot" || qt === "Slot_Token") && selectedSlot?.start && selectedSlot?.end) {
      payload.booked_slot_start = `${selectedSlot.start}:00`;
      payload.booked_slot_end = `${selectedSlot.end}:00`;
    }
    if ((qt === "Token" || qt === "Slot_Token") && typeof selectedToken?.token_number === "number") {
      payload.token_number = selectedToken.token_number;
    }
    // Session queue with time blocks: the block is the new appointment time.
    if (qt === "Session" && blocksActive && selectedBlock) {
      payload.time_block_start = selectedBlock.start;
    }
    setSubmitting(true);
    try {
      await reschedulePublicBooking(rescheduleId, payload, rescheduleToken);
      setConfirmed({
        ...snapshotBooking(rescheduleId),
        patient: rescheduleOf?.patient_name || "",
        phone: "",
        visit: "",
        manageable: true,
        rescheduled: true,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      toast.error(typeof msg === "string" ? msg : "Could not reschedule. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const runBookingFlow = async (token: string | null) => {
    if (!hospital_id || !selectedDoctor || !selectedAvailabilityId) return;
    const session = availabilities.find(
      (a) => a.availability_id === selectedAvailabilityId
    );
    if (!session) return;

    const fullPhone = `${countryCode}${phone}`;
    setSubmitting(true);
    try {
      // Step 1 — resolve patient_id.
      // - OTP-disabled hospitals: patientId may already be set by the
      //   pre-form debounced autofill search. Use it if so.
      // - OTP-required hospitals: patientId is null here — search authed,
      //   then create if no match.
      let pid: string | null = patientId;
      if (bookingFor !== "self") pid = bookingFor;
      if (!pid) {
        try {
          const existing = await searchPublicPatient(
            fullPhone,
            hospital_id,
            token
          );
          if (existing?.patient_id) pid = existing.patient_id;
        } catch (searchErr: any) {
          // 403 disabled means the hospital flipped the kill-switch — must
          // not be swallowed; bubble to the outer catch so it can render
          // the unavailable screen.
          if (searchErr?.response?.status === 403) throw searchErr;
          // Otherwise: search miss / 404 is fine — fall through to create.
        }
      }

      if (!pid) {
        const created = await createPublicPatient(
          {
            hospital_id,
            full_name: fullName.trim(),
            contact_number: fullPhone,
            gender: gender ?? undefined,
            date_of_birth: isValidAge(age)
              ? moment().subtract(Number(age), "years").format("YYYY-MM-DD")
              : null,
            // Backend does strict `=== true`. Send the literal boolean only
            // when the user actively ticked the box — omit the field
            // otherwise so the row's marketing_consent stays false (and
            // marketing_consent_at stays null) per DPDP/GDPR convention.
            ...(marketingConsent ? { marketing_consent: true } : {}),
          },
          token
        );
        pid = created?.patient_id || null;
      }
      if (!pid) {
        toast.error("Could not create patient. Please try again.");
        return;
      }

      // Existing person (self or a family member): what was typed on the form
      // is the latest truth, so write it onto their record before booking --
      // the admin screen does the same (updatePatient). Only fields with a
      // value are sent, so an empty box never blanks anything. Best-effort:
      // the appointment must not fail because this did.
      if (pid) {
        const person =
          bookingFor === "self"
            ? household?.self ?? null
            : household?.relations.find((x) => x.patient_id === pid) ?? null;
        const dob = isValidAge(age) ? moment().subtract(Number(age), "years").format("YYYY-MM-DD") : undefined;
        const recordYears = person?.date_of_birth ? moment().diff(moment(person.date_of_birth), "years") : null;
        const changes: { full_name?: string; gender?: "Male" | "Female"; date_of_birth?: string } = {};
        if (fullName.trim() && fullName.trim() !== (person?.full_name || "").trim()) changes.full_name = fullName.trim();
        if (gender && gender !== person?.gender) changes.gender = gender;
        if (dob && (recordYears === null || String(recordYears) !== age)) changes.date_of_birth = dob;
        if (Object.keys(changes).length > 0) {
          try {
            const updated = await updatePublicPatient(pid, { hospital_id, contact_number: fullPhone, ...changes }, token);
            setHousehold((h) =>
              h
                ? {
                    ...h,
                    self: h.self?.patient_id === pid ? { ...h.self, ...updated } : h.self,
                    relations: h.relations.map((r) => (r.patient_id === pid ? { ...r, ...updated } : r)),
                  }
                : h
            );
          } catch {
            // keep booking
          }
        }
      }

      // Step 2 — build booking payload (admin's shape — see memory note
      // `project_express_validator_optional.md` for the queue-type contract).
      const sessionStart = formatTime12Compact(
        session.session_start_time || session.start_time || ""
      );
      const sessionEnd = formatTime12Compact(
        session.session_end_time || session.end_time || ""
      );

      const queueType = session.queue_type || "Slot";
      const perQueue: Record<string, string | number> = {};
      if (queueType === "Slot" || queueType === "Slot_Token") {
        if (selectedSlot?.start && selectedSlot?.end) {
          perQueue.booked_slot_start = `${selectedSlot.start}:00`;
          perQueue.booked_slot_end = `${selectedSlot.end}:00`;
        }
        if (
          queueType === "Slot_Token" &&
          typeof selectedToken?.token_number === "number"
        ) {
          perQueue.token_number = selectedToken.token_number;
        }
      } else if (queueType === "Token") {
        perQueue.booked_slot_start = sessionStart;
        perQueue.booked_slot_end = sessionEnd;
        if (typeof selectedToken?.token_number === "number") {
          perQueue.token_number = selectedToken.token_number;
        }
      } else if (queueType === "Session") {
        perQueue.booked_slot_start = sessionStart;
        perQueue.booked_slot_end = sessionEnd;
      }
      if (blocksActive && selectedBlock) {
        perQueue.time_block_start = selectedBlock.start;
        perQueue.time_block_end = selectedBlock.end;
      }

      // Hospital number chosen from their own system: link it BEFORE the
      // booking, so the push the booking triggers names the existing chart
      // (admin: rememberMrn, awaited before the booking for the same reason).
      if (pid && pendingMrn && himsLookupOn) {
        try {
          await linkPublicHimsPatient(
            { hospital_id, contact_number: fullPhone, patient_id: pid, mrn: pendingMrn },
            token
          );
        } catch {
          // The booking must not fail because the link did; the desk can link later.
        }
      }

      // Hardcoded fields that differ from admin's AppointmentBooking:
      //   - booking_mode: always "booking" (no walk-in path on the public
      //     widget — see project_public_booking_otp_flag memory).
      //   - status: always 0 (pending). Admin uses 1 for walk-ins because
      //     the patient is already physically present; here they aren't.
      //   - visit_type_id: 1 for New, 2 for Follow Up — matches admin's
      //     mapping at AppointmentBooking.tsx:~1298.
      const result: any = await createPublicBooking(
        {
          hospital_id,
          doctor_id: selectedDoctor.doctor_id,
          availability_id: selectedAvailabilityId,
          patient_id: pid,
          booked_date: appointmentDate,
          booking_mode: "booking",
          booking_type: "physical",
          booking_lang: messageLang,
          is_confirmed: true,
          symptoms: "",
          comments: comments.trim(),
          queue_type: queueType,
          status: 0,
          visit_type_id: visitType === "Follow Up" ? 2 : 1,
          // PRN only travels with a Follow Up; a blank box sends nothing.
          ...(showFollowUp && visitType === "Follow Up" && prn.trim() ? { prn_number: prn.trim() } : {}),
          booking_session_start_time: sessionStart,
          booking_session_end_time: sessionEnd,
          booking_time: moment().format("YYYY-MM-DD HH:mm:ss.SSS"),
          ...perQueue,
        },
        token
      );

      const ref =
        result?.booking_id ||
        result?.id ||
        result?.result?.booking_id ||
        "";
      // Programs ride on the booking row that now exists (admin: tagNewBooking).
      if (ref && selectedProgramIds.length > 0) {
        try {
          await setPublicBookingPrograms(
            { hospital_id, contact_number: fullPhone, booking_id: String(ref), program_ids: selectedProgramIds },
            token
          );
        } catch {
          // Tagging is best-effort; the appointment itself is booked.
        }
      }
      // The booking's own page token (status / manage / pay), kept for this tab.
      const pageToken = typeof result?.page_token === "string" ? result.page_token : result?.result?.page_token;
      if (ref && pageToken) {
        try {
          window.sessionStorage.setItem(bookingPageTokenKey(String(ref)), pageToken);
        } catch {
          /* storage blocked: the WhatsApp buttons still open the pages */
        }
      }
      const pay = result?.payment || result?.result?.payment;
      // The address bar becomes this booking's own page, so a refresh (or the
      // back button) shows the booking -- not a fresh form from the WhatsApp
      // link, which is one-time in production anyway. The confirmation screen
      // stays on view; its "View my booking" is the same page.
      if (ref && pageToken) {
        try {
          window.history.replaceState(window.history.state, "", publicHref(`/${hospital_id}/manage/${ref}`));
        } catch {
          /* old browser: the refresh just shows the form again */
        }
      }
      setConfirmed({
        ...snapshotBooking(String(ref || "")),
        manageable: !!(ref && pageToken),
        payment: pay && pay.required
          ? { amount: pay.amount ?? null, currency: pay.currency, link: pay.link || null, expires_at: pay.expires_at || null }
          : null,
      });
      resetForm();
    } catch (err: any) {
      const status = err?.response?.status;
      const rawError = err?.response?.data?.error;
      const errMsg = typeof rawError === "string" ? rawError : "";

      // 403 = hospital flipped the public-booking kill-switch mid-session.
      // Drop the booking_token and render the unavailable screen — same UI
      // as if the flag had been off when the widget loaded.
      if (status === 403 && /disabled/i.test(errMsg)) {
        setBookingToken(null);
        setForcedDisabled(true);
        return;
      }

      // 429 variants — backend disambiguates between hospital-cap, phone-cap
      // and the generic IP/phone burst limit via the error string.
      //   - hospital / phone caps are session-fatal for today: clear the
      //     token, reset to start, tell the patient to try tomorrow / call.
      //   - generic burst limit ("Too many requests") just needs a wait;
      //     don't reset the form so the patient can retry without re-typing.
      if (status === 429) {
        const phone = formatHospitalPhone(branding?.contact_number);
        if (/for this hospital/i.test(errMsg)) {
          setBookingToken(null);
          toast.error(
            phone
              ? `This hospital is full for today. Try again tomorrow or call them at ${phone}.`
              : "This hospital is full for today. Try again tomorrow or call them."
          );
          resetForm();
        } else if (/for this phone number/i.test(errMsg)) {
          setBookingToken(null);
          toast.error(
            "You've reached today's booking limit for this number. Try tomorrow."
          );
          resetForm();
        } else {
          // Generic IP/phone burst limit — server returns either a
          // 1-minute or 10-minute hint in the error string.
          const tenMin = /10\s*minutes?/i.test(errMsg);
          toast.error(
            tenMin
              ? "Slow down — try again in 10 minutes."
              : "Slow down — try again in a minute."
          );
        }
        return;
      }

      // 400 with the advance-days message is a fallback — the date picker
      // should normally clamp to public_max_advance_days, but if the flag
      // was missing or stale, the backend will reject and we surface the
      // friendlier wording here.
      if (status === 400 && /in advance/i.test(errMsg)) {
        const n = flags?.public_max_advance_days;
        toast.error(
          n != null
            ? `Please pick a date within the next ${n} days.`
            : "Please pick a date the hospital is currently accepting bookings for."
        );
        return;
      }

      // 409 = idempotency replay. Same (patient_id, availability_id,
      // appointment_date) was submitted within the backend's 60s dedup
      // window — could be a double-click or a retry after a flake. The
      // server returns the original booking row in the error body, so
      // we surface it as a success instead of a failure.
      if (status === 409) {
        const dup =
          err?.response?.data?.result?.booking_id ||
          err?.response?.data?.booking_id ||
          "";
        // Only a replay of THIS booking (the server returns its id) is a success.
        // Any other 409 is a refusal -- e.g. the hospital's one-booking-per-day
        // rule -- and must not show "Booking confirmed" (15 Oct 2026, prod).
        if (dup) {
          setConfirmed(snapshotBooking(String(dup)));
          resetForm();
          return;
        }
        const refusal = err?.response?.data?.message || errMsg;
        toast.error(
          typeof refusal === "string" && refusal
            ? refusal
            : "This appointment could not be booked. Please pick another time or contact the hospital."
        );
        return;
      }
      // 401 on a gated call after a successful verify means the booking_token
      // (10-min TTL) has expired or been invalidated. Drop it and send the
      // user back through Phase 1; the canonical spec wants the phone field
      // to stay filled, which it does because we only clear bookingToken.
      if (status === 401 && otpRequired) {
        setBookingToken(null);
        clearStoredSession(hospital_id);
        if (waSession === "active") {
          // The WhatsApp session ran out mid-booking: back to WhatsApp for a
          // fresh link rather than an OTP they were promised not to need.
          setBookingToken(null);
          setWaSession("expired");
          return;
        }
        setOtpPurpose("book");
        setOtpOpen(true);
        toast.error("Session expired. Please verify your number again.");
        return;
      }
      const msg =
        err?.response?.data?.error?.[0]?.msg ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  // Called by OtpModal when /verify-otp returns 200. Park the token and
  // immediately kick off the booking flow (the OTP modal opens *after*
  // the form is already fully validated, so we don't need any further
  // user input — go straight to Phase 2/3).
  const handleOtpVerified = (token: string) => {
    setBookingToken(token);
    if (hospital_id) writeStoredSession(hospital_id, token, 10 * 60);
    setOtpOpen(false);
    // Verified at step 1: the household/record loaders pick the token up and
    // run; the booking happens later. Verified at Book: carry on as before.
    if (otpPurpose === "book") runBookingFlow(token);
  };

  // The phone is usable for personal data once it is complete AND either the
  // hospital has OTP off or this session verified it (booking_token present).
  const phoneComplete = isValidNationalLength(countryCode, phone);
  const isWhatsAppSession = waSession === "active" && !!bookingToken;
  // Direct visitors on an OTP hospital never load personal data before Book
  // (same as before); OTP-off hospitals keep their old pre-form prefill.
  const phoneUsable = phoneComplete && (isWhatsAppSession || !otpRequired);
  const fullPhoneNow = `${countryCode}${phone}`;

  // Fill a person's details into the form. Only EMPTY boxes are filled when
  // `onlyEmpty` is set, so a value the patient just typed is never overwritten
  // by a background load (the WhatsApp link may already have filled them).
  const fillFromPerson = (p: HouseholdPerson | null, onlyEmpty: boolean) => {
    if (!p) return;
    if (p.full_name && (!onlyEmpty || !fullName.trim())) setFullName(p.full_name.trim());
    if ((p.gender === "Male" || p.gender === "Female") && (!onlyEmpty || !gender)) setGender(p.gender);
    if (p.date_of_birth && (!onlyEmpty || !age)) {
      const yrs = moment().diff(moment(p.date_of_birth), "years");
      if (yrs >= 0 && yrs <= MAX_AGE) setAge(String(yrs));
    }
  };

  // Household load: replaces the old debounced /patient/search prefill and also
  // covers OTP-required hospitals, which that search could not (it 401'd).
  useEffect(() => {
    if (!hospital_id || !phoneUsable) {
      setHousehold(null);
      setPatientId(null);
      return;
    }
    let cancelled = false;
    setHouseholdLoading(true);
    const t = setTimeout(async () => {
      try {
        const h = await getPublicHousehold(fullPhoneNow, hospital_id, bookingToken);
        if (cancelled) return;
        setHousehold(h);
        if (bookingFor === "self") {
          setPatientId(h.self?.patient_id ?? null);
          fillFromPerson(h.self, true);
        }
      } catch (e: any) {
        if (cancelled) return;
        if (e?.response?.status === 403) {
          setForcedDisabled(true);
          return;
        }
        setHousehold(null);
        setPatientId(null);
      } finally {
        if (!cancelled) setHouseholdLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospital_id, phoneUsable, fullPhoneNow, bookingToken]);

  // Appointment programs catalog (flag-gated; off = empty list, no chips).
  useEffect(() => {
    if (!hospital_id || !programsOn) {
      setProgramOptions([]);
      setSelectedProgramIds([]);
      return;
    }
    let cancelled = false;
    getPublicPrograms(hospital_id)
      .then((r) => {
        if (cancelled) return;
        setProgramOptions(r.enabled ? r.programs : []);
      })
      .catch(() => {
        if (!cancelled) setProgramOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [hospital_id, programsOn]);

  // Picking a person: book under THEIR patient row and show their details.
  const choosePerson = (choice: "self" | HouseholdPerson) => {
    setPendingMrn(null);
    setHimsResetToken((t) => t + 1);
    if (choice === "self") {
      setBookingFor("self");
      setPatientId(household?.self?.patient_id ?? null);
      setFullName(household?.self?.full_name?.trim() || "");
      setGender(household?.self?.gender === "Male" || household?.self?.gender === "Female" ? household.self.gender : null);
      setAge("");
      fillFromPerson(household?.self ?? null, false);
      return;
    }
    setBookingFor(choice.patient_id);
    setPatientId(choice.patient_id);
    setFullName(choice.full_name?.trim() || "");
    setAge("");
    setGender(null);
    fillFromPerson(choice, false);
  };

  // "+ Add someone" in the popup: creates their patient row + the relation
  // right away (admin's Add Relation does the same), then selects them.
  const handleAddMember = async (form: HouseholdAddForm) => {
    if (!hospital_id) return;
    const created = await addPublicHouseholdMember(
      {
        hospital_id,
        contact_number: fullPhoneNow,
        full_name: form.full_name,
        gender: form.gender ?? undefined,
        date_of_birth: isValidAge(form.age) ? moment().subtract(Number(form.age), "years").format("YYYY-MM-DD") : null,
        type: form.type,
        ...(form.relation_contact_number ? { relation_contact_number: form.relation_contact_number } : {}),
        ...(form.booker_full_name ? { booker_full_name: form.booker_full_name } : {}),
      },
      bookingToken
    );
    const person = created.patient;
    setHousehold((h) =>
      h
        ? { ...h, relations: [...h.relations.filter((r) => r.patient_id !== person.patient_id), person] }
        : { self: null, relations: [person], verified: true }
    );
    setBookingFor(person.patient_id);
    setPatientId(person.patient_id);
    setFullName(person.full_name?.trim() || form.full_name);
    setGender(form.gender);
    setAge(isValidAge(form.age) ? String(Number(form.age)) : "");
    setPendingMrn(null);
    setHimsResetToken((t) => t + 1);
    toast.success(`${form.full_name} added`);
  };

  // A record chosen from the hospital's own system (admin: applyHimsPatient).
  // Identity comes from the linked DocTrue patient when there is one; the phone
  // match alone is not used, since one mobile is a household.
  const applyHimsPatient = (p: PublicHimsPatient, _matched: PublicHimsMatched | null) => {
    setPendingMrn(p.mrn);
    if (p.full_name?.trim()) setFullName(p.full_name.trim());
    if (p.gender === "Male" || p.gender === "Female") setGender(p.gender);
    if (p.age !== null && p.age !== undefined && p.age >= 0 && p.age <= MAX_AGE) setAge(String(p.age));
    else if (p.date_of_birth) {
      const yrs = moment().diff(moment(p.date_of_birth), "years");
      if (yrs >= 0 && yrs <= MAX_AGE) setAge(String(yrs));
    }
    if (p.linked_patient?.patient_id) {
      setBookingFor(p.linked_patient.patient_id);
      setPatientId(p.linked_patient.patient_id);
    }
    toast.success("Details filled from your hospital record");
  };

  // Where the WhatsApp confirmation will land for the chosen person.
  const confirmationPhone = (): string => {
    if (bookingFor === "self") return fullPhoneNow;
    const r = household?.relations.find((x) => x.patient_id === bookingFor);
    return r?.contact_number ? String(r.contact_number) : fullPhoneNow;
  };
  const formatIntl = (digits: string) => {
    const split = splitPhone(digits);
    return split ? `+${split.code} ${split.national}` : `+${digits}`;
  };

  const relationLabel = (): string => {
    if (bookingFor === "self") return "";
    const r = household?.relations.find((x) => x.patient_id === bookingFor);
    return r?.relationship_type || "Family member";
  };

  // ── Data-fetching effects ────────────────────────────────────────
  // Three-stage cascade:
  //   1. hospital_id → branding + flags  (sets up the page chrome)
  //   2. hospital_id + date → online doctors for that date
  //   3. mapping_id → availabilities (sessions) for the selected doctor
  // Each stage uses the cancelled-flag pattern so that a rapid date or
  // doctor change doesn't race in a stale response.

  // 1. on hospital_id load: parallel fetch hospital + feature flags
  useEffect(() => {
    if (!hospital_id) return;
    let cancelled = false;
    setInitialLoading(true);
    setInitialError(null);

    Promise.all([
      getPublicHospital(hospital_id).catch(() => null),
      getPublicFeatureFlags(hospital_id).catch(() => null),
    ])
      .then(([brand, ff]) => {
        if (cancelled) return;
        if (!brand) {
          setInitialError(
            "We couldn't find this hospital. Please check your booking link."
          );
        } else {
          setBranding(brand);
          setFlags(ff);
        }
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hospital_id]);

  // 1b. reschedule mode: the booking being moved (its doctor, its current time,
  //     and whether the hospital lets it move at all).
  useEffect(() => {
    if (!rescheduleId || !hospital_id) return;
    if (!rescheduleToken) {
      setRescheduleError("Please open Manage booking from your WhatsApp message again.");
      return;
    }
    let cancelled = false;
    getPublicBookingDetail(rescheduleId, hospital_id, rescheduleToken)
      .then((d) => {
        if (cancelled) return;
        if (!d.manage?.can_reschedule) {
          const why = d.manage?.reschedule_blocked_reason || "disabled";
          setRescheduleError(RESCHEDULE_BLOCKED_TEXT[why] || RESCHEDULE_BLOCKED_TEXT.disabled);
          return;
        }
        if (!d.mapping_id) {
          setRescheduleError("This doctor can't be rescheduled online. Please contact the hospital.");
          return;
        }
        setRescheduleOf(d);
        setMappingId(d.mapping_id);
        setStep(1);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const msg = e?.response?.data?.error;
        setRescheduleError(typeof msg === "string" ? msg : "Could not load this booking.");
      });
    return () => {
      cancelled = true;
    };
  }, [rescheduleId, rescheduleToken, hospital_id]);

  // 2. the doctor list. Date-first: refetched per date, each doctor flagged
  //    for that day, and the chosen doctor resets with the date. Doctor-first:
  //    fetched once (today's flags are ignored); changing the date keeps the
  //    doctor, since the date is chosen FROM the doctor's working days.
  const doctorListDate = docFirst ? todayStr() : appointmentDate;
  useEffect(() => {
    if (!hospital_id || !doctorListDate) return;
    let cancelled = false;
    setDoctorsLoading(true);
    if (!docFirst) {
      setMappingId(null);
      setAvailabilities([]);
      setSelectedAvailabilityId(null);
    }

    // All doctors, each flagged for the day; falls back to the online-only
    // list if the newer route is missing (older backend).
    getPublicDoctors(hospital_id, doctorListDate)
      .catch((err) => {
        if (err?.response?.status === 404) return getPublicOnlineDoctors(hospital_id, doctorListDate);
        throw err;
      })
      .then((list) => {
        if (cancelled) return;
        setDoctors(list);
        // Date-first preselects the first doctor of the day -- not when the
        // patient is meant to choose through a specialization.
        if (!docFirst && doctorPicker !== "specialization") {
          const firstAvailable = list.find((d) => d.available_on_date !== false && d.bookable !== false);
          if (firstAvailable) setMappingId(firstAvailable.mapping_id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isKillSwitchError(err)) {
          setForcedDisabled(true);
          return;
        }
        setDoctors([]);
      })
      .finally(() => {
        if (!cancelled) setDoctorsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospital_id, doctorListDate, docFirst]);

  // 2b. doctor-first: the chosen doctor's working dates; the date jumps to the
  //     first of them when the current one is not among them.
  useEffect(() => {
    if (!docFirst || !mappingId) {
      setAvailableDates([]);
      return;
    }
    let cancelled = false;
    setAvailableDatesLoading(true);
    const windowDays = maxAppointmentDate
      ? Math.min(90, Math.max(1, moment(maxAppointmentDate).diff(moment(todayStr()), "days") + 1))
      : 45;
    getPublicDoctorAvailableDates(mappingId, todayStr(), windowDays)
      .then((all) => {
        if (cancelled) return;
        const dates = maxAppointmentDate ? all.filter((dt) => dt <= maxAppointmentDate) : all;
        setAvailableDates(dates);
        if (dates.length > 0 && !dates.includes(appointmentDate)) setAppointmentDate(dates[0]);
      })
      .catch(() => {
        if (!cancelled) setAvailableDates([]);
      })
      .finally(() => {
        if (!cancelled) setAvailableDatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docFirst, mappingId, maxAppointmentDate]);

  // 3. on doctor select: fetch sessions for mapping_id
  useEffect(() => {
    if (!mappingId) {
      setAvailabilities([]);
      setSelectedAvailabilityId(null);
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    setSelectedAvailabilityId(null);

    getPublicDoctorAvailability(mappingId)
      .then((list) => {
        if (cancelled) return;
        setAvailabilities(list);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isKillSwitchError(err)) {
          setForcedDisabled(true);
          return;
        }
        setAvailabilities([]);
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mappingId]);

  if (initialLoading || (rescheduling && !rescheduleOf && !rescheduleError)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CenteredCard>
          <p className="text-queueText">Loading…</p>
        </CenteredCard>
      </div>
    );
  }

  if (initialError || !branding) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CenteredCard>
          <h2 className="text-lg font-semibold text-modalText mb-1">
            Hospital not found
          </h2>
          <p className="text-sm text-queueText">
            {initialError || "Please check your booking link."}
          </p>
        </CenteredCard>
      </div>
    );
  }

  // Master kill-switch — render the unavailable screen and stop. Either the
  // initial flag fetch returned `public_booking_enabled: false`, or a later
  // call returned 403 (the hospital flipped the switch mid-session).
  if (publicDisabled) {
    return <UnavailableScreen branding={branding} />;
  }

  if (confirmed) {
    return (
      <ConfirmedScreen
        branding={branding}
        booking={confirmed}
        fromWhatsApp={waSession === "active" || rescheduling}
        onBookAnother={() => {
          if (confirmed.rescheduled) {
            window.location.href = publicHref(`/${hospital_id}`);
            return;
          }
          // Back to the booking form's own address (the bar shows the booking's page).
          try {
            window.history.replaceState(window.history.state, "", publicHref(`/${hospital_id}`));
          } catch {
            /* ignore */
          }
          setConfirmed(null);
        }}
      />
    );
  }

  if (rescheduling && rescheduleError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CenteredCard>
          <h2 className="text-lg font-semibold text-modalText mb-1">Can't reschedule online</h2>
          <p className="text-sm text-queueText">{rescheduleError}</p>
          <a
            href={publicHref(`/${hospital_id}/manage/${rescheduleId}`)}
            className="mt-4 inline-flex items-center justify-center w-full h-11 rounded-lg border border-[#0F3CBE] text-[#0F3CBE] text-sm font-semibold"
          >
            Back to my booking
          </a>
        </CenteredCard>
      </div>
    );
  }

  if (waSession === "expired") {
    return (
      <ExpiredLinkScreen branding={branding} />
    );
  }


  // ── Field fragments ──────────────────────────────────────────────
  // The same inputs serve two layouts: the two-column card on md+ and the
  // one-step-at-a-time flow on phones. Each is defined once so both layouts
  // share state, validation and behaviour exactly.
  const nameField = (
    <Field label={bookingFor === "self" ? "Full Name" : "Patient's full name"} required>
      <input
        type="text"
        placeholder={bookingFor === "self" ? "Enter full name" : "Enter the patient's full name"}
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        className="w-full h-11 md:h-10 border border-doctorsBorder rounded-lg px-3.5 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
      />
    </Field>
  );

  // Phone number (drives OTP target + patient lookup). The country code is
  // required by the backend's E.164-without-+ contract. Helper text warns
  // that OTP arrives on WhatsApp — they need a number that has WhatsApp.
  const phoneField = (
    <Field label="Phone Number" required>
      <div className="flex gap-2">
        <PhoneNumberSelect value={countryCode} setValue={setCountryCode} options={countryOptions} />
        <input
          type="tel"
          inputMode="numeric"
          maxLength={maxNationalLength(countryCode) + 1}
          placeholder="Enter phone number"
          value={phone}
          onChange={(e) => {
            // maxLength catches typing in most browsers; the slice catches
            // paste / IME / autofill that can bypass the attribute.
            const next = cleanNationalInput(countryCode, e.target.value);
            if (next !== phone) {
              // Whatever was verified (OTP or the WhatsApp link) was for the
              // old number; family members and records belong to it too.
              setBookingToken(null);
              clearStoredSession(hospital_id);
              setWaSession("none");
              setBookingFor("self");
              setPatientId(null);
            }
            setPhone(next);
          }}
          className="flex-1 min-w-0 h-11 md:h-10 border border-doctorsBorder rounded-lg px-3.5 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
        />
      </div>
      {isWhatsAppSession ? (
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-[#1FAD63] font-semibold">
          <FaWhatsapp className="w-3 h-3 flex-shrink-0" />
          Verified via WhatsApp
        </p>
      ) : (
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-queueText">
          <FaWhatsapp className="w-3 h-3 text-[#1FAD63] flex-shrink-0" />
          OTP will be sent on WhatsApp — please enter your WhatsApp number.
        </p>
      )}
    </Field>
  );

  // Phone verification at step 1 (OTP hospitals only). Family members and the
  // hospital record are personal data, so they unlock after the code.
  // One line: who the booking is for, and a link to the popup that changes it
  // or adds someone (admin: "Booking For" + the Manage Relation modal).
  const bookingForLine =
    isWhatsAppSession && phoneUsable ? (
      <div className="mt-2 flex items-center justify-between gap-3 text-[12px]">
        <p className="min-w-0 truncate text-modalText">
          {householdLoading && !household ? (
            <span className="text-queueText">Loading…</span>
          ) : (
            <>
              <span className="text-queueText">Booking for </span>
              <span className="font-semibold">
                {bookingFor === "self" ? "yourself" : `${fullName.trim() || "family member"} (${relationLabel()})`}
              </span>
              {bookingFor !== "self" && confirmationPhone() !== fullPhoneNow && (
                <span className="text-queueText"> · confirmation to {formatIntl(confirmationPhone())}</span>
              )}
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => setHouseholdOpen(true)}
          className="shrink-0 font-semibold text-[#0F3CBE] underline underline-offset-2"
        >
          {bookingFor === "self" ? "Book for someone else" : "Change"}
        </button>
      </div>
    ) : null;

  // The hospital's own patient record (MRN), flag-gated; renders nothing otherwise.
  const himsField = (
    <PublicHimsLookup
      hospitalId={hospital_id}
      enabled={himsLookupOn && isWhatsAppSession}
      mobile={isWhatsAppSession && phoneUsable ? fullPhoneNow : ""}
      bookingToken={bookingToken}
      onApply={applyHimsPatient}
      resetToken={himsResetToken}
    />
  );

  // Age + Gender share a row: narrow numeric input, 2-pill segmented control.
  const ageGenderFields = (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-5 sm:col-span-4">
        <Field label="Age" required>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={3}
            placeholder="Age"
            value={age}
            // Digits only, 0–150; the validator shows the message, this keeps junk out.
            onChange={(e) => setAge(e.target.value.replace(/\D/g, "").slice(0, 3))}
            className="w-full h-11 md:h-10 border border-doctorsBorder rounded-lg px-3.5 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
          />
        </Field>
      </div>
      <div className="col-span-7 sm:col-span-8">
        <Field label="Gender" required>
          <div className="grid grid-cols-2 gap-2">
            <PillButton selected={gender === "Male"} onClick={() => setGender("Male")} label="Male" />
            <PillButton selected={gender === "Female"} onClick={() => setGender("Female")} label="Female" />
          </div>
        </Field>
      </div>
    </div>
  );

  // Language of the WhatsApp messages for this booking (booking_lang). Starts on
  // the patient's chat language (?lang= from the bot). Only the hospital's enabled
  // languages, only when there is a choice; the page itself stays English.
  const messageLangOptions = (flags?.language_change_buttons || ["en"]).filter((l) => MESSAGE_LANG_NAMES[l]);
  const languageField =
    messageLangOptions.length > 1 ? (
      <Field label="WhatsApp updates in">
        <select
          value={messageLangOptions.includes(messageLang) ? messageLang : "en"}
          onChange={(e) => setMessageLang(e.target.value)}
          className="w-full h-11 md:h-10 border border-doctorsBorder rounded-lg px-3 text-sm md:text-[15px] bg-white focus:outline-none focus:border-[#0F3CBE]"
        >
          {messageLangOptions.map((l) => (
            <option key={l} value={l}>
              {MESSAGE_LANG_NAMES[l]}
            </option>
          ))}
        </select>
      </Field>
    ) : null;

  // Visit Type — New vs Follow Up → visit_type_id (1 = New, 2 = Follow Up).
  // Follow Up only shows when the hospital tracks PRN-based revisits.
  const visitTypeField = (
    <Field label="Visit Type" required>
      <div className="grid grid-cols-2 gap-2">
        <PillButton selected={visitType === "New"} onClick={() => setVisitType("New")} label="New" />
        {showFollowUp && (
          <PillButton selected={visitType === "Follow Up"} onClick={() => setVisitType("Follow Up")} label="Follow Up" />
        )}
      </div>
      <p className="mt-1.5 text-[11px] md:text-xs text-queueText">
        Your details are shared only with this hospital and kept private.
      </p>
      {showFollowUp && visitType === "Follow Up" && (
        <div className="mt-3">
          <p className="text-modalText text-[13px] md:text-sm mb-1 font-semibold">PRN Number</p>
          <input
            type="text"
            inputMode="text"
            maxLength={40}
            placeholder="Enter your PRN (optional)"
            value={prn}
            onChange={(e) => setPrn(e.target.value)}
            className="w-full h-11 md:h-10 border border-doctorsBorder rounded-lg px-3.5 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
          />
          <p className="mt-1 text-[11px] md:text-xs text-queueText">
            Found on your previous prescription or bill from this hospital.
          </p>
        </div>
      )}
    </Field>
  );

  // Distinct specializations among the listed doctors ("Dermatology, Pediatrics"
  // counts as two), with how many doctors each has.
  // web_whatsapp_v2, date-first: a doctor with nothing left to book that day
  // (no session, all sessions over, or every slot / token taken) is left out of
  // the lists, instead of letting the patient find out on the next step. The
  // server only sends `bookable` for those hospitals; everyone else is unchanged.
  const hideUnbookable = flags?.web_whatsapp_v2 === true && !docFirst;
  const listedDoctors = hideUnbookable
    ? doctors.filter((d) => d.available_on_date !== false && d.bookable !== false)
    : doctors;
  const specializations = (() => {
    const counts = new Map<string, number>();
    listedDoctors.forEach((d) =>
      String(d.specialization || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((x) => counts.set(x, (counts.get(x) || 0) + 1))
    );
    return Array.from(counts.keys()).sort((a, b) => a.localeCompare(b));
  })();
  // Date-first: the day's flag decides; doctor-first: everyone is pickable.
  const doctorAvailable = (d: PublicDoctor) => docFirst || (d.available_on_date !== false && d.bookable !== false);
  const availableDoctors = doctors.filter(doctorAvailable);

  const pickDoctor = (d: PublicDoctor) => {
    setMappingId(d.mapping_id);
    setDoctorInfoOpen(false);
    setDoctorPickerOpen(false);
    setSpecPickerOpen(false);
    setDoctorSearch("");
  };

  // The doctor list box, the specialization box (specialization → its doctors),
  // or both -- public_booking_flow. Each opens a popup. With one specialization
  // or none there is nothing to choose, so the list box stands in.
  const hasSpecChoice = specializations.length > 1;
  const showDoctorList = doctorPicker !== "specialization" || !hasSpecChoice;
  const showSpecBox = doctorPicker !== "doctor" && hasSpecChoice;
  const specOnly = showSpecBox && !showDoctorList;
  const specBox = (
    <button
      type="button"
      disabled={doctorsLoading || listedDoctors.length === 0}
      onClick={() => setSpecPickerOpen(true)}
      className={`${specOnly ? "h-full" : "mt-2"} w-full flex items-center justify-between gap-2 px-3 py-2 md:px-3.5 rounded-lg border border-doctorsBorder bg-white text-left hover:border-[#0F3CBE] disabled:opacity-60 disabled:cursor-not-allowed`}
    >
      <span className="min-w-0">
        <span className="block text-sm md:text-[15px] font-medium text-modalText truncate">
          {specOnly
            ? selectedDoctor
              ? doctorName(selectedDoctor)
              : doctorsLoading
                ? "Loading…"
                : "Choose a specialization"
            : "By specialization"}
        </span>
        <span className="block text-xs md:text-[13px] text-queueText truncate">
          {specOnly
            ? specialization || selectedDoctor?.specialization || `${specializations.length} specializations`
            : specialization || `${specializations.length} specializations`}
        </span>
      </span>
      <FiChevronRight className="w-4 h-4 text-queueText flex-shrink-0" />
    </button>
  );
  const doctorPickerBoxes = (
    <Field label={specOnly ? "Specialization and doctor" : "Choose Doctor"} required>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 min-w-0">
          {specOnly ? (
            specBox
          ) : (
            <DoctorPickerTrigger
              doctor={selectedDoctor}
              loading={doctorsLoading}
              empty={doctors.length === 0 || listedDoctors.length === 0}
              noneAvailable={!docFirst && !doctorsLoading && doctors.length > 0 && availableDoctors.length === 0}
              onClick={() => setDoctorPickerOpen(true)}
            />
          )}
        </div>
        {selectedDoctor && (
          <InfoPopover
            open={doctorInfoOpen}
            onToggle={() => setDoctorInfoOpen((v) => !v)}
            onClose={() => setDoctorInfoOpen(false)}
            label="Doctor details"
            buttonClassName={`h-full w-11 md:w-10 rounded-lg border flex items-center justify-center transition-colors ${
              doctorInfoOpen ? "border-[#0F3CBE] bg-[#EAF0FF] text-[#0F3CBE]" : "border-doctorsBorder text-queueText hover:border-[#0F3CBE] hover:text-[#0F3CBE]"
            }`}
          >
            <DoctorDetails doctor={selectedDoctor} />
          </InfoPopover>
        )}
      </div>
      {showSpecBox && !specOnly && specBox}
    </Field>
  );

  // Rescheduling keeps the doctor: shown, not picked.
  const doctorBoxes = rescheduling ? (
    <Field label="Doctor">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-doctorsBorder bg-[#FAFBFF] px-3.5 py-2.5">
        <div className="min-w-0">
          <p className="text-sm md:text-[15px] font-semibold text-modalText truncate">
            {selectedDoctor ? doctorName(selectedDoctor) : doctorsLoading ? "Loading…" : "Your doctor"}
          </p>
          {selectedDoctor?.specialization && <p className="text-[12px] text-queueText truncate">{selectedDoctor.specialization}</p>}
        </div>
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-queueText">Same doctor</span>
      </div>
    </Field>
  ) : (
    doctorPickerBoxes
  );

  const dateInput = (
    <input
      type="date"
      min={todayStr()}
      max={maxAppointmentDate}
      value={appointmentDate}
      onChange={(e) => setAppointmentDate(e.target.value)}
      className="w-full flex-1 min-w-0 border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE]"
    />
  );

  // Doctor-first: the doctor's next working dates as tappable chips under the
  // date box, so a doctor who is off for days is booked on a day they do work.
  const availableDateChips = docFirst && selectedDoctor ? (
    <div className="mt-2">
      {availableDatesLoading ? (
        <p className="text-[12px] text-queueText">Checking {doctorName(selectedDoctor)}'s days…</p>
      ) : availableDates.length === 0 ? (
        <p className="text-[12px] text-[#B45309] font-medium">No available dates for {doctorName(selectedDoctor)} in the booking window.</p>
      ) : (
        <>
          <p className="text-[11px] text-queueText mb-1">Next available</p>
          <div className="flex gap-1.5 overflow-x-auto pb-0.5">
            {availableDates.slice(0, 8).map((d) => (
              <PillChip key={d} selected={appointmentDate === d} onClick={() => setAppointmentDate(d)} label={moment(d).format("ddd D MMM")} />
            ))}
          </div>
        </>
      )}
    </div>
  ) : null;
  const dateField = (
    <Field label="Appointment Date" required>
      {dateInput}
      {availableDateChips}
    </Field>
  );
  // A function, not a const: sessionSelect is declared further down and this
  // is only evaluated at render time.
  const dateRow = () => (
    <Field label="Appointment Date" required>
      <div className="flex gap-2">
        {dateInput}
        {sessionSelect}
      </div>
      {availableDateChips}
    </Field>
  );

  // Session list is filtered by date (sessionsForDate memo — Sun=1..Sat=7).
  const sessionSelect = (
    <select
      value={selectedAvailabilityId || ""}
      onChange={(e) => setSelectedAvailabilityId(e.target.value || null)}
      disabled={!selectedDoctor || availabilityLoading || sessionsForDate.length === 0}
      className="w-full flex-1 min-w-0 border border-doctorsBorder rounded-lg px-3.5 py-2.5 md:py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE] bg-white disabled:bg-gray-50 disabled:text-queueText"
    >
      {!selectedDoctor && <option value="">Select a doctor first</option>}
      {selectedDoctor && availabilityLoading && <option value="">Loading sessions…</option>}
      {selectedDoctor && !availabilityLoading && sessionsForDate.length === 0 && (
        <option value="">{allSessionsEndedToday ? "Today's sessions have ended — pick another date" : "No session for this day"}</option>
      )}
      {sessionsForDate.map((a) => {
        const start = a.session_start_time || a.start_time || "";
        const end = a.session_end_time || a.end_time || "";
        const label = start || end ? `${formatTime(start)} - ${formatTime(end)}` : "Session";
        return (
          <option key={a.availability_id} value={a.availability_id}>
            {label}
          </option>
        );
      })}
    </select>
  );

  // Slot vs Token vs Session — branches on the chosen session's queue_type.
  // Which free units fall inside a block: a slot by its start, a token by its
  // estimated time (session start + (n − 1) × wait, the server's rule).
  const hmToMin = (t: string | undefined | null): number | null => {
    if (!t) return null;
    const [h, m] = String(t).split(":").map(Number);
    return Number.isNaN(h) ? null : h * 60 + (m || 0);
  };
  const selectedSessionRow = availabilities.find((a) => a.availability_id === selectedAvailabilityId) || null;
  const sessionWait = selectedSessionRow ? waitMinutesOf(selectedSessionRow.wait_time) : null;
  const sessionStartMin = hmToMin(selectedSessionRow?.session_start_time || selectedSessionRow?.start_time);
  const freeSlotsInBlock = (b: PublicTimeBlock) => {
    const bs = hmToMin(b.start), be = hmToMin(b.end);
    if (bs === null || be === null) return [];
    return slots.filter((sl) => {
      const st = hmToMin(sl.start);
      return sl.available !== false && st !== null && st >= bs && st < be;
    });
  };
  const freeTokensInBlock = (b: PublicTimeBlock) => {
    const bs = hmToMin(b.start), be = hmToMin(b.end);
    if (bs === null || be === null || sessionStartMin === null || !sessionWait) return [];
    return tokens.filter((t) => {
      const st = sessionStartMin + (t.token_number - 1) * sessionWait;
      return t.available !== false && st >= bs && st < be;
    });
  };
  // Session queues have no slot or token: the block itself is the booking's
  // time. On today, a block whose end has passed is not offered; nor is a block
  // whose share of the session cap is taken.
  const sessionBlockOpen = (b: PublicTimeBlock) =>
    !b.full && (!isTodaySelected || atTime(appointmentDate, b.end).isAfter(moment(nowTick)));
  // Blocks apply to slots always, to tokens only when the wait time is known
  // (without it a token cannot be placed in a block), and to Session queues.
  const blocksActive =
    timeBlocks.active &&
    (slotTokenKind === "slots" || slotTokenKind === "session" || (slotTokenKind === "tokens" && !!sessionWait && sessionStartMin !== null));
  const chooseBlock = (b: PublicTimeBlock) => {
    setSelectedBlock(b);
    if (slotTokenKind === "session") return;
    if (slotTokenKind === "slots") {
      const first = freeSlotsInBlock(b).sort((x, y) => (hmToMin(x.start) || 0) - (hmToMin(y.start) || 0))[0] || null;
      setSelectedSlot(first);
    } else {
      const first = freeTokensInBlock(b).sort((x, y) => x.token_number - y.token_number)[0] || null;
      setSelectedToken(first);
    }
  };
  const blockLabel = (b: PublicTimeBlock) => `${formatTime(b.start)} - ${formatTime(b.end)}`;
  // Token sessions: the block plus the token ("4:00 PM - 5:00 PM · Token 19").
  // Slot / Slot_Token: the block only -- the exact slot inside it is the
  // clinic's business, the patient is promised the window.
  const blockWithUnitLabel = (b: PublicTimeBlock) =>
    slotTokenKind === "tokens" && selectedToken ? `${blockLabel(b)} · Token ${selectedToken.token_number}` : blockLabel(b);
  const timeBlockField = blocksActive ? (
    <Field label={slotTokenLoading ? "Loading…" : "Choose a time"} required={!slotTokenLoading}>
      {slotTokenLoading ? (
        <p className="text-sm text-queueText">Loading…</p>
      ) : (
        (() => {
          const rows = timeBlocks.blocks.map((b) => ({
            b,
            free:
              slotTokenKind === "slots"
                ? freeSlotsInBlock(b).length
                : slotTokenKind === "session"
                  ? (sessionBlockOpen(b) ? 1 : 0)
                  : freeTokensInBlock(b).length,
          }));
          if (rows.every((r) => r.free === 0)) {
            return (
              <p className="text-sm text-[#B45309] font-medium">
                {isTodaySelected ? "No time left for today. Please pick another date." : "This session is fully booked."}
              </p>
            );
          }
          return (
            <div className="grid grid-cols-2 gap-2">
              {rows.map(({ b, free }) => {
                const on = selectedBlock?.index === b.index;
                return (
                  <button
                    key={b.index}
                    type="button"
                    disabled={free === 0}
                    aria-pressed={on}
                    onClick={() => chooseBlock(b)}
                    className={`h-11 md:h-10 px-2 rounded-lg border text-[13px] font-semibold transition-colors ${
                      free === 0
                        ? "border-doctorsBorder bg-[#F7F7F9] text-queueText cursor-not-allowed line-through"
                        : on
                          ? "bg-[#0F3CBE] border-[#0F3CBE] text-white"
                          : "bg-white border-doctorsBorder text-modalText hover:border-[#0F3CBE]"
                    }`}
                  >
                    {blockLabel(b)}
                  </button>
                );
              })}
            </div>
          );
        })()
      )}
      {selectedBlock && (
        <p className="mt-1.5 text-[11px] text-queueText">
          Your appointment will be confirmed for {blockWithUnitLabel(selectedBlock)}.
        </p>
      )}
    </Field>
  ) : null;

  const slotTokenFields = (
    <>
      {timeBlockField}
      {blocksActive ? null : slotTokenKind === "slots" && (
        <Field label={slotTokenLoading ? "Loading slots…" : "Choose slot timing"} required={!slotTokenLoading}>
          <SlotGrid
            slots={slots}
            selected={selectedSlot}
            onSelect={setSelectedSlot}
            loading={slotTokenLoading}
            emptyText={isTodaySelected ? "No slots left for today. Please pick another date." : "No slots available for this session."}
          />
        </Field>
      )}
      {blocksActive ? null : slotTokenKind === "tokens" && (
        <Field label={slotTokenLoading ? "Loading tokens…" : "Choose Token"} required={!slotTokenLoading}>
          <TokenGrid emptyText={isTodaySelected ? "No tokens left for today. Please pick another date." : "No tokens available for this session."} tokens={tokens} selected={selectedToken} onSelect={setSelectedToken} loading={slotTokenLoading} />
        </Field>
      )}
      {slotTokenKind === "session" && !blocksActive && (
        <p className="text-xs text-queueText mt-3">
          Session booking — no slot or token selection needed.
        </p>
      )}
    </>
  );

  // Appointment programs (admin: "Appointment Programs" chips), optional.
  // One row that opens a popup, so the step stays short.
  const selectedProgramNames = programOptions
    .filter((p) => selectedProgramIds.includes(p.program_id))
    .map((p) => p.program_name);
  const programsField =
    !rescheduling && programsOn && programOptions.length > 0 ? (
      <Field label="Appointment Programs">
        <button
          type="button"
          onClick={() => setProgramsOpen(true)}
          className="w-full flex items-center justify-between gap-2 h-11 md:h-10 px-3.5 rounded-lg border border-doctorsBorder bg-white text-left hover:border-[#0F3CBE]"
        >
          <span className={`min-w-0 truncate text-sm md:text-[15px] ${selectedProgramNames.length ? "text-modalText" : "text-queueText"}`}>
            {selectedProgramNames.length ? selectedProgramNames.join(", ") : "None selected (optional)"}
          </span>
          <FiChevronRight className="w-4 h-4 text-queueText flex-shrink-0" />
        </button>
      </Field>
    ) : null;

  const commentsField = rescheduling ? null : (
    <Field label="Comments">
      <textarea
        placeholder="Anything the doctor should know? (optional)"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        rows={2}
        className="w-full border border-doctorsBorder rounded-lg px-3.5 py-2 text-sm md:text-[15px] focus:outline-none focus:border-[#0F3CBE] resize-y min-h-[56px] md:min-h-[52px]"
      />
    </Field>
  );

  // Marketing consent defaults unticked (DPDP/GDPR); optional, never gates Submit.
  const consentField = rescheduling ? null : (
    <label className="flex items-start gap-2 text-xs md:text-[12px] text-queueText cursor-pointer select-none sm:max-w-[60%]">
      <input
        type="checkbox"
        checked={marketingConsent}
        onChange={(e) => setMarketingConsent(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-doctorsBorder accent-[#0F3CBE] cursor-pointer flex-shrink-0"
      />
      <span className="leading-snug">
        Send me updates, offers, and health tips from this hospital on WhatsApp.
      </span>
    </label>
  );

  // Reschedule: the appointment being moved, in place of the patient form.
  const currentAppointmentCard = rescheduleOf ? (
    <div className="mt-2 rounded-lg border border-doctorsBorder bg-[#FAFBFF] px-3.5 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0F3CBE]">Current appointment</p>
      <p className="mt-1 text-sm font-semibold text-modalText">{rescheduleOf.patient_name || "—"}</p>
      <p className="text-sm text-modalText">{moment(rescheduleOf.booked_date).format("ddd, D MMM YYYY")}</p>
      <p className="text-sm text-queueText">
        {rescheduleOf.time_block_start && rescheduleOf.time_block_end
          ? `${formatTime(rescheduleOf.time_block_start)} - ${formatTime(rescheduleOf.time_block_end)}`
          : rescheduleOf.booked_slot_start && rescheduleOf.queue_type !== "Token"
            ? `${formatTime(rescheduleOf.booked_slot_start)} - ${formatTime(rescheduleOf.booked_slot_end || "")}`
            : `${formatTime(rescheduleOf.booking_session_start_time || "")} - ${formatTime(rescheduleOf.booking_session_end_time || "")}`}
        {rescheduleOf.token_number ? ` · Token ${rescheduleOf.token_number}` : ""}
      </p>
      <a href={publicHref(`/${hospital_id}/manage/${rescheduleId}`)} className="mt-2 inline-block text-xs font-semibold text-[#0F3CBE]">
        Keep this time
      </a>
    </div>
  ) : null;

  // ── Desktop: one card, two columns (unchanged layout) ────────────
  const desktopLayout = (
    <>
      <div className="bg-white rounded-2xl border border-doctorsBorder mt-4 md:mt-3 md:shadow-[0_8px_32px_-16px_rgba(15,60,190,0.2)] overflow-hidden">
        <div className="h-0.5 md:h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
        <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-doctorsBorder">
          <div className="flex flex-col p-5 md:p-5 lg:p-6">
            {rescheduling ? (
              <>
                <SectionHeader eyebrow="Reschedule" title="Move your appointment" subtitle="Pick a new date and time with the same doctor." />
                {currentAppointmentCard}
              </>
            ) : (
              <>
                <SectionHeader eyebrow="Step 1" title="Patient Information" subtitle="Tell us who's booking the appointment." />
                {phoneField}
                {bookingForLine}
                {nameField}
                {ageGenderFields}
                {languageField}
                {himsField}
                {visitTypeField}
              </>
            )}
          </div>
          <div className="flex flex-col p-5 md:p-5 lg:p-6">
            <SectionHeader
              eyebrow={rescheduling ? "New time" : "Step 2"}
              title={rescheduling ? "Pick a new date and time" : "Appointment Details"}
              subtitle={rescheduling ? "Same doctor. Choose one of their days, then a time." : "Pick your doctor, date and slot."}
            />
            {docFirst ? (
              <>
                {doctorBoxes}
                {dateRow()}
              </>
            ) : (
              <>
                {dateRow()}
                {doctorBoxes}
              </>
            )}
            {slotTokenFields}
            {programsField}
            {commentsField}
          </div>
        </div>
      </div>

      <div className="mt-4 md:mt-3 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-between items-stretch sm:items-center md:bg-white md:rounded-xl md:border md:border-doctorsBorder md:px-4 md:py-2.5 md:shadow-[0_4px_20px_-12px_rgba(15,60,190,0.18)]">
        {consentField}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          {!rescheduling && (
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2.5 md:py-2 rounded-lg border border-[#0F3CBE] text-[#0F3CBE] bg-white font-semibold text-sm md:text-[15px] hover:bg-[#EAF0FF] transition-colors"
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full sm:w-auto sm:min-w-[200px] px-5 py-2.5 md:py-2 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm md:text-[15px] hover:bg-[#0a2f99] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
          >
            {submitting ? (rescheduling ? "Saving…" : "Booking…") : rescheduling ? "Confirm new time" : "Book Appointment"}
          </button>
        </div>
      </div>
    </>
  );

  // ── Mobile: one step at a time, ending in a summary ──────────────
  const goNext = () => {
    if (step === 0 && !validatePatientStep()) return;
    if (step === 1 && !validateDoctorStep()) return;
    if (step === 2 && !validateTimeStep()) return;
    setStep((n) => Math.min(n + 1, MOBILE_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const firstStep = rescheduling ? 1 : 0;
  const goBack = () => {
    setStep((n) => Math.max(n - 1, firstStep));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectedSession =
    availabilities.find((a) => a.availability_id === selectedAvailabilityId) || null;
  const sessionLabel = (() => {
    if (!selectedSession) return "—";
    const start = selectedSession.session_start_time || selectedSession.start_time || "";
    const end = selectedSession.session_end_time || selectedSession.end_time || "";
    return start || end ? `${formatTime(start)} - ${formatTime(end)}` : "Session";
  })();
  const timeLabel = blocksActive && selectedBlock
    ? blockWithUnitLabel(selectedBlock)
    : slotTokenKind === "slots"
      ? selectedSlot
        ? `${formatTime(selectedSlot.start)} - ${formatTime(selectedSlot.end)}`
        : "—"
      : slotTokenKind === "tokens"
        ? selectedToken
          ? `Token ${selectedToken.token_number}`
          : "—"
        : slotTokenKind === "session"
          ? "Any time within the session"
          : "—";

  // Summary, kept short: two cards, values inline, one Edit per group.
  const patientName = rescheduling ? rescheduleOf?.patient_name || "" : fullName.trim();
  const phoneLine = phone ? formatIntl(confirmationPhone()) : "";
  const visitLine = showFollowUp && !rescheduling
    ? `${visitType}${visitType === "Follow Up" && prn.trim() ? ` · PRN ${prn.trim()}` : ""}`
    : "";
  const patientMeta = rescheduling
    ? ""
    : [relationLabel(), age ? `${age} yrs` : "", gender || "", visitLine ? (visitType === "New" ? "New visit" : visitLine) : ""]
        .filter(Boolean)
        .join(" · ");
  const commentsLine = comments.trim();

  const isLastStep = step === MOBILE_STEPS.length - 1;
  const mobileFlow = (
    <>
      <div className="bg-white rounded-2xl border border-doctorsBorder mt-4 overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
        <div className="px-5 pt-4 flex gap-1.5" aria-hidden>
          {MOBILE_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#0F3CBE]" : "bg-gray-200"}`}
            />
          ))}
        </div>
        <div className="flex flex-col p-5 pt-3">
          <SectionHeader
            eyebrow={`Step ${step + 1} of ${MOBILE_STEPS.length}`}
            title={step === 1 && rescheduling ? "New date" : step === 1 && docFirst ? "Doctor and date" : MOBILE_STEPS[step].title}
            subtitle={
              step === 1 && rescheduling
                ? "Same doctor. Pick one of their available days."
                : step === 1 && docFirst
                  ? "Pick your doctor, then one of their available days."
                  : MOBILE_STEPS[step].subtitle
            }
          />
          {step === 0 && (
            <>
              {phoneField}
              {bookingForLine}
              {nameField}
              {ageGenderFields}
              {languageField}
              {himsField}
              {visitTypeField}
            </>
          )}
          {step === 1 && (docFirst ? (
            <>
              {currentAppointmentCard}
              {doctorBoxes}
              {dateField}
            </>
          ) : (
            <>
              {dateField}
              {doctorBoxes}
            </>
          ))}
          {step === 2 && (
            <>
              {/* Whom and when, carried over from step 2 so the time step reads on its own. */}
              <div className="mt-4 md:mt-3.5 rounded-lg border border-doctorsBorder bg-[#FAFBFF] px-3.5 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-modalText truncate">{selectedDoctor ? doctorName(selectedDoctor) : "—"}</p>
                  <p className="text-[12px] text-queueText truncate">
                    {[selectedDoctor?.specialization, moment(appointmentDate).format("ddd, D MMM YYYY")].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <button type="button" onClick={() => setStep(1)} className="shrink-0 text-xs font-semibold text-[#0F3CBE]">
                  Change
                </button>
              </div>
              <Field label="Session" required>{sessionSelect}</Field>
              {slotTokenFields}
              {programsField}
              {commentsField}
            </>
          )}
          {isLastStep && (
            <>
              <div className="mt-4 space-y-3">
                {/* Appointment first: it is what the patient is confirming. */}
                <div className="rounded-xl border border-[#0F3CBE]/25 bg-[#F5F8FF] overflow-hidden">
                  <div className="px-4 pt-3.5 pb-3 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0F3CBE] text-white flex items-center justify-center flex-shrink-0">
                      <FiUser className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold text-modalText leading-snug break-words">
                        {selectedDoctor ? doctorName(selectedDoctor) : "—"}
                      </p>
                      {selectedDoctor?.specialization && (
                        <p className="text-[12px] text-queueText truncate">{selectedDoctor.specialization}</p>
                      )}
                    </div>
                    <button type="button" onClick={() => setStep(1)} className="text-xs font-semibold text-[#0F3CBE] flex-shrink-0 pt-0.5">
                      Edit
                    </button>
                  </div>
                  <div className="mx-4 border-t border-dashed border-[#0F3CBE]/25" />
                  <div className="px-4 py-3 grid grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-queueText">
                        <FiCalendar className="w-3.5 h-3.5" /> Date
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-modalText">{moment(appointmentDate).format("ddd, D MMM")}</p>
                      <p className="text-[12px] text-queueText">{moment(appointmentDate).format("YYYY")}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-queueText">
                        <FiClock className="w-3.5 h-3.5" /> {slotTokenKind === "tokens" && !blocksActive ? "Token" : "Time"}
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-[#0F3CBE] break-words">{timeLabel}</p>
                      <p className="text-[12px] text-queueText">Session {sessionLabel}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full px-4 py-2.5 border-t border-[#0F3CBE]/15 bg-white/60 text-xs font-semibold text-[#0F3CBE] flex items-center justify-center gap-1"
                  >
                    Change time <FiChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="rounded-xl border border-doctorsBorder bg-white px-4 py-3.5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#EAF0FF] text-[#0F3CBE] flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      {(patientName || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold text-modalText leading-snug break-words">{patientName || "—"}</p>
                      {patientMeta && <p className="text-[12px] text-queueText break-words">{patientMeta}</p>}
                    </div>
                    {!rescheduling && (
                      <button type="button" onClick={() => setStep(0)} className="text-xs font-semibold text-[#0F3CBE] flex-shrink-0 pt-0.5">
                        Edit
                      </button>
                    )}
                  </div>
                  {(phoneLine || selectedProgramNames.length > 0 || commentsLine) && (
                    <div className="mt-3 pt-3 border-t border-doctorsBorder space-y-1.5">
                      {phoneLine && (
                        <p className="flex items-center gap-2 text-sm text-modalText">
                          <FiPhone className="w-3.5 h-3.5 text-queueText flex-shrink-0" /> {phoneLine}
                        </p>
                      )}
                      {!rescheduling && selectedProgramNames.length > 0 && (
                        <p className="flex items-start gap-2 text-sm text-modalText">
                          <FiLayers className="w-3.5 h-3.5 text-queueText flex-shrink-0 mt-[3px]" />
                          <span className="break-words">{selectedProgramNames.join(", ")}</span>
                        </p>
                      )}
                      {commentsLine && (
                        <p className="flex items-start gap-2 text-sm text-queueText">
                          <FiMessageSquare className="w-3.5 h-3.5 flex-shrink-0 mt-[3px]" />
                          <span className="break-words">“{commentsLine}”</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4">{consentField}</div>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        {/* No Back on the first step: there is nowhere to go back to. */}
        {step > firstStep && (
          <button
            type="button"
            onClick={goBack}
            className="w-[34%] px-5 py-3 rounded-lg border border-[#0F3CBE] text-[#0F3CBE] bg-white font-semibold text-sm"
          >
            Back
          </button>
        )}
        {isLastStep ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-5 py-3 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
          >
            {submitting ? (rescheduling ? "Saving…" : "Booking…") : rescheduling ? "Confirm new time" : "Book Appointment"}
          </button>
        ) : (
          <button
            type="button"
            onClick={goNext}
            className="flex-1 px-5 py-3 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
          >
            Next
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE]">
      <main className="flex-1 max-w-5xl lg:max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-5 lg:py-6">
        <PageTitle hospitalName={branding.hospital_name} reschedule={rescheduling} />

        {/* Standalone hospital identity strip — mirrors admin's page-level
            header. Subtle elevation on md+ to align with the form cards
            below. */}
        <CardHeader branding={branding} />

        {isMobile ? mobileFlow : desktopLayout}
      </main>

      <DoctorPickerModal
        open={doctorPickerOpen}
        onClose={() => {
          setDoctorPickerOpen(false);
          setDoctorSearch("");
        }}
        doctors={listedDoctors}
        selectedMappingId={mappingId}
        search={doctorSearch}
        setSearch={setDoctorSearch}
        specializations={specializations}
        specialization={specialization}
        setSpecialization={setSpecialization}
        ignoreAvailability={docFirst}
        onPick={pickDoctor}
      />
      <SpecializationPickerModal
        open={specPickerOpen}
        onClose={() => setSpecPickerOpen(false)}
        specializations={specializations}
        doctors={listedDoctors}
        selectedMappingId={mappingId}
        initialSpecialization={specialization}
        isAvailable={doctorAvailable}
        onPick={(sp, d) => {
          setSpecialization(sp);
          pickDoctor(d);
        }}
      />


      <ProgramsModal
        open={programsOpen}
        onClose={() => setProgramsOpen(false)}
        programs={programOptions}
        selectedIds={selectedProgramIds}
        onToggle={(id) =>
          setSelectedProgramIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
        }
      />

      <HouseholdModal
        open={householdOpen}
        onClose={() => setHouseholdOpen(false)}
        household={household}
        selected={bookingFor}
        bookerPhone={fullPhoneNow}
        defaultCountryCode={countryCode}
        countryOptions={countryOptions}
        onSelect={(choice) => {
          choosePerson(choice);
          setHouseholdOpen(false);
        }}
        onAdd={async (form) => {
          await handleAddMember(form);
          setHouseholdOpen(false);
        }}
      />

      <OtpModal
        open={otpOpen}
        phone={`${countryCode}${phone}`}
        countryCode={countryCode}
        nationalNumber={phone}
        hospitalId={hospital_id || ""}
        hospitalPhone={formatHospitalPhone(branding?.contact_number)}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        onSkip={() => {
          // Stale flag — backend says OTP isn't enabled. Drop the modal
          // and run the booking flow without a token; the gated endpoints
          // are permissive when the hospital has the flag off.
          setOtpOpen(false);
          runBookingFlow(null);
        }}
        onPublicDisabled={() => {
          // Hospital flipped the kill-switch between widget load and OTP
          // open. Close the modal, drop the token, and flip into the
          // unavailable screen (parent re-renders).
          setOtpOpen(false);
          setBookingToken(null);
          setForcedDisabled(true);
        }}
      />
    </div>
  );
};

const overflowModalStyles = {
  overlay: {
    backgroundColor: "rgba(2, 11, 23, 0.45)",
    zIndex: 999,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: 0,
    border: "none",
    borderRadius: 12,
    minWidth: "min(92vw, 560px)",
    maxWidth: "min(92vw, 560px)",
    width: "min(92vw, 560px)",
    maxHeight: "85vh",
    overflow: "hidden",
  },
} as const;

// Inline counts chosen so the chips plus the "More" chip fill whole rows:
// slots 5 + More = 6 (2 columns on phones, 3 above), tokens 7 + More = 8 (4 columns).
const SLOT_VISIBLE_LIMIT = 5;
const TOKEN_VISIBLE_LIMIT = 7;
const SLOT_GRID = "grid grid-cols-2 sm:grid-cols-3 gap-2";
const TOKEN_GRID = "grid grid-cols-4 gap-2";
const TOKEN_GRID_WIDE = "grid grid-cols-4 sm:grid-cols-5 gap-2";

// One cell of the slot / token grid: fills its column, centred, solid blue when picked.
const gridChipClass = (selected: boolean, disabled: boolean) =>
  [
    "w-full h-10 px-1.5 rounded-lg border text-[13px] sm:text-sm font-medium tabular-nums whitespace-nowrap text-center transition-colors",
    disabled
      ? "bg-gray-100 text-gray-400 border-doctorsBorder cursor-not-allowed"
      : selected
        ? "bg-[#0F3CBE] text-white border-[#0F3CBE] font-semibold"
        : "bg-white text-modalText border-doctorsBorder hover:border-[#0F3CBE]",
  ].join(" ");

const gridMoreClass =
  "w-full h-10 px-1.5 rounded-lg border border-[#0F3CBE] bg-white text-[#0F3CBE] text-[13px] sm:text-sm font-semibold flex items-center justify-center gap-1 whitespace-nowrap hover:bg-[#EAF0FF]";

const SlotGrid: React.FC<{
  slots: PublicSlot[];
  selected: PublicSlot | null;
  onSelect: (s: PublicSlot) => void;
  loading: boolean;
  emptyText?: string;
}> = ({ slots, selected, onSelect, loading, emptyText }) => {
  const [open, setOpen] = useState(false);
  if (loading) {
    return <p className="text-sm text-queueText">Loading slots…</p>;
  }
  if (!slots.length) {
    return <p className="text-sm text-[#B45309] font-medium">{emptyText || "No slots available."}</p>;
  }
  const hasMore = slots.length > SLOT_VISIBLE_LIMIT + 1;
  // A slot picked in the "More" popup takes the last visible cell, so the
  // patient can see what they chose.
  const pickedIdx = selected ? slots.findIndex((s) => s.start === selected.start && s.end === selected.end) : -1;
  const visible = !hasMore
    ? slots
    : pickedIdx >= SLOT_VISIBLE_LIMIT
      ? [...slots.slice(0, SLOT_VISIBLE_LIMIT - 1), slots[pickedIdx]]
      : slots.slice(0, SLOT_VISIBLE_LIMIT);
  return (
    <>
      <div className={SLOT_GRID}>
        {visible.map((s, i) => {
          const isSelected =
            selected?.start === s.start && selected?.end === s.end;
          const disabled = s.available === false;
          return (
            <button
              key={`${s.start}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(s)}
              className={gridChipClass(isSelected, disabled)}
            >
              {formatTime(s.start)} - {formatTime(s.end)}
            </button>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={gridMoreClass}
          >
            More slots <FiChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        style={overflowModalStyles}
        ariaHideApp={false}
      >
        <div className="flex flex-col max-h-[85vh]">
          <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
            <p className="text-modalText font-semibold">Choose slot timing</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-queueText hover:text-modalText"
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-5">
            <div className={SLOT_GRID}>
              {slots.map((s, i) => {
                const isSelected =
                  selected?.start === s.start && selected?.end === s.end;
                const disabled = s.available === false;
                return (
                  <button
                    key={`all-${s.start}-${i}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onSelect(s);
                      setOpen(false);
                    }}
                    className={gridChipClass(isSelected, disabled)}
                  >
                    {formatTime(s.start)} - {formatTime(s.end)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const TokenGrid: React.FC<{
  tokens: PublicToken[];
  selected: PublicToken | null;
  onSelect: (t: PublicToken) => void;
  loading: boolean;
  emptyText?: string;
}> = ({ tokens, selected, onSelect, loading, emptyText }) => {
  const [open, setOpen] = useState(false);
  if (loading) {
    return <p className="text-sm text-queueText">Loading tokens…</p>;
  }
  if (!tokens.length) {
    return <p className="text-sm text-[#B45309] font-medium">{emptyText || "No tokens available."}</p>;
  }
  const hasMore = tokens.length > TOKEN_VISIBLE_LIMIT + 1;
  // A token picked in the "More" popup takes the last visible cell, so the
  // patient can see what they chose.
  const pickedIdx = selected ? tokens.findIndex((t) => t.token_number === selected.token_number) : -1;
  const visible = !hasMore
    ? tokens
    : pickedIdx >= TOKEN_VISIBLE_LIMIT
      ? [...tokens.slice(0, TOKEN_VISIBLE_LIMIT - 1), tokens[pickedIdx]]
      : tokens.slice(0, TOKEN_VISIBLE_LIMIT);
  return (
    <>
      <div className={TOKEN_GRID}>
        {visible.map((t, i) => {
          const isSelected = selected?.token_number === t.token_number;
          const disabled = t.available === false;
          return (
            <button
              key={`token-${t.token_number}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(t)}
              className={gridChipClass(isSelected, disabled)}
            >
              Token {t.token_number}
            </button>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={gridMoreClass}
          >
            More <FiChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <Modal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        style={overflowModalStyles}
        ariaHideApp={false}
      >
        <div className="flex flex-col max-h-[85vh]">
          <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
            <p className="text-modalText font-semibold">Choose Token</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-queueText hover:text-modalText"
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-5">
            <div className={TOKEN_GRID_WIDE}>
              {tokens.map((t, i) => {
                const isSelected = selected?.token_number === t.token_number;
                const disabled = t.available === false;
                return (
                  <button
                    key={`all-token-${t.token_number}-${i}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onSelect(t);
                      setOpen(false);
                    }}
                    className={gridChipClass(isSelected, disabled)}
                  >
                    Token {t.token_number}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

// SectionHeader is the strong heading block for each form section.
// Three layers of hierarchy in a small footprint:
//   1. Eyebrow — small uppercase brand-color tag ("Step 1") that lets
//      the user clock the section count at a glance
//   2. Title — bold modalText, size:xl
//   3. Subtitle — muted queueText, brief one-liner explaining the goal
// Together they create a clear "this is a section" signal without
// needing a divider in the body.
const SectionHeader: React.FC<{
  eyebrow: string;
  title: string;
  subtitle?: string;
}> = ({ eyebrow, title, subtitle }) => (
  <div className="mb-1">
    <div className="flex items-center gap-2">
      <span
        className="h-0.5 w-5 rounded-full bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0]"
        aria-hidden
      />
      <p className="text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.16em] text-[#0F3CBE]">
        {eyebrow}
      </p>
    </div>
    <h2 className="mt-1.5 text-lg md:text-xl font-bold text-modalText tracking-tight leading-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-0.5 text-xs md:text-[13px] text-queueText leading-snug">
        {subtitle}
      </p>
    )}
  </div>
);

// Field renders an admin-style labelled input row. Label uses the bold
// modalText treatment from AppointmentBooking; required fields get the
// red asterisk shifted up a hair to align with the cap height of the
// label (matches admin's `relative bottom-1` micro-adjustment).
const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <div>
    {/* mt-8 was admin's spacing; tightened to mt-5/md:mt-4 here so the
        whole form fits in one viewport on tablet+ desktop without scroll. */}
    <p className="text-modalText text-[13px] md:text-sm mt-4 md:mt-3.5 font-semibold">
      {label}
      {required && (
        <span className="text-red-500 text-sm relative bottom-0.5 font-medium ml-0.5">
          *
        </span>
      )}
    </p>
    <div className="mt-1">{children}</div>
  </div>
);

// PillButton matches AppointmentBooking's segmented-control style:
// bordered card that fills its grid cell, switching to solid #0F3CBE on
// select. Hover preview tints unselected pills so the user can feel
// where their tap will land.
const PillButton: React.FC<{
  selected: boolean;
  onClick: () => void;
  label: string;
}> = ({ selected, onClick, label }) => (
  <div
    onClick={onClick}
    className={[
      "h-11 md:h-10 px-3 rounded-lg border border-doctorsBorder flex items-center justify-center cursor-pointer w-full select-none transition-colors",
      // Picked = the same solid blue as the Next / Book button.
      selected
        ? "bg-[#0F3CBE] text-white border-[#0F3CBE]"
        : "bg-white text-modalText hover:border-[#0F3CBE] hover:text-[#0F3CBE]",
    ].join(" ")}
    role="button"
    aria-pressed={selected}
  >
    <p className="font-semibold text-sm md:text-[15px]">{label}</p>
  </div>
);

// A WhatsApp link works once. Opened again (forwarded, re-tapped, or after its
// session ran out) the patient is told to send "hi" for a fresh one -- the only
// way forward (no "book without WhatsApp" escape to the OTP page).
const ExpiredLinkScreen: React.FC<{
  branding: HospitalBranding | null;
}> = ({ branding }) => {
  const digits = String(branding?.contact_number || "").replace(/\D/g, "");
  const waHref = digits ? `https://wa.me/${digits}?text=${encodeURIComponent("hi")}` : "";
  return (
    <div className="min-h-screen bg-[#F7F8FC] flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-doctorsBorder overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#FFF4E5] flex items-center justify-center">
            <FiX className="w-6 h-6 text-[#D97706]" />
          </div>
          <h1 className="mt-4 text-lg font-bold text-modalText">This link has expired</h1>
          <p className="mt-2 text-sm text-queueText">
            Booking links from WhatsApp work only once. Send <span className="font-semibold text-modalText">hi</span>
            {branding?.hospital_name ? ` to ${branding.hospital_name}` : ""} on WhatsApp to get a new one.
          </p>
          {waHref && (
            <a
              href={waHref}
              className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#1FAD63] text-white text-sm font-semibold"
            >
              <FaWhatsapp className="w-4 h-4" /> Open WhatsApp
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// What the "+ Add someone" form inside the popup submits.
interface HouseholdAddForm {
  full_name: string;
  age: string;
  gender: Gender | null;
  type: RelationType;
  relation_contact_number?: string;
  booker_full_name?: string;
}

// "Who is this appointment for?" popup: Me, each saved family member, and an
// add form. Mirrors the admin Manage Relation / Add Relation modals, kept off
// the main form so the booking page stays short.
const HouseholdModal: React.FC<{
  open: boolean;
  onClose: () => void;
  household: Household | null;
  selected: "self" | string;
  bookerPhone: string;
  defaultCountryCode: string;
  countryOptions: string[];
  onSelect: (choice: "self" | HouseholdPerson) => void;
  onAdd: (form: HouseholdAddForm) => Promise<void>;
}> = ({ open, onClose, household, selected, bookerPhone, defaultCountryCode, countryOptions, onSelect, onAdd }) => {
  const [mode, setMode] = useState<"list" | "add">("list");
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);
  const [type, setType] = useState<RelationType | "">("");
  const [code, setCode] = useState(defaultCountryCode);
  const [phone, setPhone] = useState("");
  useEffect(() => {
    setPhone((p) => cleanNationalInput(code, p));
  }, [code]);
  const [bookerName, setBookerName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("list");
    setName("");
    setAge("");
    setGender(null);
    setType("");
    setCode(defaultCountryCode);
    setPhone("");
    setBookerName("");
    setSaving(false);
  }, [open, defaultCountryCode]);

  const fmt = (digits: string) => {
    const split = splitPhone(digits);
    return split ? `+${split.code} ${split.national}` : `+${digits}`;
  };
  // Relation type implies gender for all but "Other" (same as admin).
  const pickType = (t: RelationType | "") => {
    setType(t);
    if (["Father", "Brother", "Husband", "Son"].includes(t)) setGender("Male");
    else if (["Mother", "Sister", "Wife", "Daughter"].includes(t)) setGender("Female");
  };
  const submit = async () => {
    if (!name.trim()) return toast.error("Please enter their name.");
    if (!type) return toast.error("Please select the relationship.");
    if (!gender) return toast.error("Please select a gender.");
    if (age && !isValidAge(age)) return toast.error(`Please enter a valid age (0 to ${MAX_AGE}).`);
    if (phone && !isValidNationalLength(code, phone)) {
      return toast.error(`Their phone number must be ${nationalLengths(code).join(" or ")} digits for +${code}.`);
    }
    if (phone && `${code}${phone}` === bookerPhone) return toast.error("That is your own number. Leave it empty to receive the confirmation yourself.");
    if (!household?.self && !bookerName.trim()) return toast.error("Please enter your own name as well.");
    setSaving(true);
    try {
      await onAdd({
        full_name: name.trim(),
        age,
        gender,
        type,
        ...(phone ? { relation_contact_number: `${code}${phone}` } : {}),
        ...(bookerName.trim() ? { booker_full_name: bookerName.trim() } : {}),
      });
    } catch (e: any) {
      const msg = e?.response?.data?.error;
      toast.error(typeof msg === "string" ? msg : "Could not add the family member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const row = (key: string, title: string, sub: string, isSel: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left ${
        isSel ? "border-[#0F3CBE] bg-[#F3F6FF]" : "border-doctorsBorder bg-white hover:border-[#0F3CBE]"
      }`}
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-modalText truncate">{title}</span>
        <span className="block text-[11px] text-queueText truncate">{sub}</span>
      </span>
      {isSel && <FiCheck className="w-4 h-4 shrink-0 text-[#0F3CBE]" />}
    </button>
  );
  const inputCls = "w-full h-10 border border-doctorsBorder rounded-lg px-3 text-sm focus:outline-none focus:border-[#0F3CBE]";

  return (
    <Modal isOpen={open} onRequestClose={onClose} style={doctorPickerModalStyles} ariaHideApp={false}>
      <div className="w-[92vw] max-w-md bg-white rounded-2xl overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-doctorsBorder">
          <p className="text-sm font-bold text-modalText">
            {mode === "list" ? "Who is this appointment for?" : "Add a family member"}
          </p>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-queueText hover:text-modalText">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {mode === "list" ? (
          <div className="p-4 flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            {row(
              "self",
              household?.self?.full_name?.trim() ? `Me · ${household.self.full_name.trim()}` : "Me",
              fmt(bookerPhone),
              selected === "self",
              () => onSelect("self")
            )}
            {(household?.relations || []).map((r) =>
              row(
                r.patient_id,
                [r.full_name?.trim() || "Family member", r.relationship_type].filter(Boolean).join(" · "),
                r.contact_number ? `Confirmation to ${fmt(String(r.contact_number))}` : "Confirmation to your number",
                selected === r.patient_id,
                () => onSelect(r)
              )
            )}
            <button
              type="button"
              onClick={() => setMode("add")}
              className="mt-1 h-10 rounded-lg border border-dashed border-[#0F3CBE] text-[#0F3CBE] text-sm font-semibold hover:bg-[#F3F6FF]"
            >
              + Add someone
            </button>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-3 max-h-[70vh] overflow-y-auto">
            <div>
              <p className="text-[13px] font-semibold text-modalText mb-1">Full name <span className="text-red-500">*</span></p>
              <input className={inputCls} placeholder="Their full name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-modalText mb-1">Relationship to you <span className="text-red-500">*</span></p>
              <select className={`${inputCls} bg-white`} value={type} onChange={(e) => pickType(e.target.value as RelationType | "")}>
                <option value="">Select relation</option>
                {RELATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-5">
                <p className="text-[13px] font-semibold text-modalText mb-1">Age</p>
                <input
                  className={inputCls}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  placeholder="Age"
                  value={age}
                  onChange={(e) => setAge(e.target.value.replace(/\D/g, "").slice(0, 3))}
                />
              </div>
              <div className="col-span-7">
                <p className="text-[13px] font-semibold text-modalText mb-1">Gender <span className="text-red-500">*</span></p>
                <div className="grid grid-cols-2 gap-2">
                  <PillChip selected={gender === "Male"} onClick={() => setGender("Male")} label="Male" />
                  <PillChip selected={gender === "Female"} onClick={() => setGender("Female")} label="Female" />
                </div>
              </div>
            </div>
            <div>
              <p className="text-[13px] font-semibold text-modalText mb-1">Their phone number (optional)</p>
              <div className="flex gap-2">
                <PhoneNumberSelect value={code} setValue={setCode} options={countryOptions} />
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={maxNationalLength(code) + 1}
                  className={`${inputCls} flex-1 min-w-0`}
                  placeholder="If they should get the confirmation"
                  value={phone}
                  onChange={(e) => setPhone(cleanNationalInput(code, e.target.value))}
                />
              </div>
              <p className="mt-1 text-[11px] text-queueText">Leave empty and the confirmation comes to your number.</p>
            </div>
            {!household?.self && (
              <div>
                <p className="text-[13px] font-semibold text-modalText mb-1">Your name <span className="text-red-500">*</span></p>
                <input className={inputCls} placeholder="The person booking" value={bookerName} onChange={(e) => setBookerName(e.target.value)} />
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setMode("list")} className="h-10 flex-1 rounded-lg border border-doctorsBorder text-sm font-semibold text-modalText">
                Back
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="h-10 flex-1 rounded-lg bg-[#0F3CBE] text-white text-sm font-semibold disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save and select"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Appointment programs popup: tick any number, Done closes it.
const ProgramsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  programs: PublicProgram[];
  selectedIds: number[];
  onToggle: (id: number) => void;
}> = ({ open, onClose, programs, selectedIds, onToggle }) => (
  <Modal isOpen={open} onRequestClose={onClose} style={doctorPickerModalStyles} ariaHideApp={false}>
    <div className="w-full bg-white flex flex-col max-h-[80vh]">
      <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3 border-b border-doctorsBorder">
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-modalText">Appointment programs</p>
          <p className="text-[12px] text-queueText mt-0.5">Optional. Pick any that apply to this visit.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-1 -mr-1 text-queueText hover:text-modalText">
          <FiX className="w-5 h-5" />
        </button>
      </div>
      <div className="px-5 py-4 flex flex-col gap-2 overflow-y-auto">
        {programs.map((p) => {
          const on = selectedIds.includes(p.program_id);
          return (
            <button
              key={p.program_id}
              type="button"
              onClick={() => onToggle(p.program_id)}
              aria-pressed={on}
              title={p.program_code}
              className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left ${
                on ? "border-[#0F3CBE] bg-[#F3F6FF]" : "border-doctorsBorder bg-white hover:border-[#0F3CBE]"
              }`}
            >
              <span className="text-sm font-semibold text-modalText truncate">{p.program_name}</span>
              <span
                className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${
                  on ? "bg-[#0F3CBE] border-[#0F3CBE] text-white" : "border-doctorsBorder bg-white"
                }`}
              >
                {on && <FiCheck className="w-3.5 h-3.5" />}
              </span>
            </button>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-doctorsBorder">
        <button type="button" onClick={onClose} className="h-11 w-full rounded-lg bg-[#0F3CBE] text-white text-sm font-semibold">
          {selectedIds.length ? `Done · ${selectedIds.length} selected` : "Skip"}
        </button>
      </div>
    </div>
  </Modal>
);

// Compact selectable chip for the "who is this for" row.
const PillChip: React.FC<{ selected: boolean; onClick: () => void; label: string }> = ({ selected, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={[
      // shrink-0: inside a horizontal scroller the chips must keep their width
      // and the row scrolls, instead of every chip collapsing to one letter.
      "h-9 px-3 rounded-full border text-[13px] font-semibold transition-colors shrink-0 whitespace-nowrap",
      selected
        ? "bg-[#0F3CBE] border-[#0F3CBE] text-white"
        : "bg-white border-doctorsBorder text-modalText hover:border-[#0F3CBE] hover:text-[#0F3CBE]",
    ].join(" ")}
  >
    {label}
  </button>
);

// Shared modal styles for the OTP modal (was originally defined for the
// doctor picker — name kept for minimal churn since the dimensions
// happen to be a good fit for the OTP modal too).
const doctorPickerModalStyles = {
  overlay: {
    backgroundColor: "rgba(2, 11, 23, 0.45)",
    zIndex: 999,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: 0,
    border: "none",
    borderRadius: 12,
    minWidth: "min(92vw, 520px)",
    maxWidth: "min(92vw, 520px)",
    width: "min(92vw, 520px)",
    maxHeight: "85vh",
    overflow: "hidden",
  },
} as const;

// DoctorPickerTrigger — the row that sits in the right card under "Choose
// Doctor". Empty state shows hint copy; filled state shows the selected
// doctor's avatar + name + specialization. Tap opens the picker modal.
const DoctorPickerTrigger: React.FC<{
  doctor: PublicDoctor | null;
  loading: boolean;
  empty: boolean;
  /** Doctors exist but none has a session that day: the list still opens so the patient sees who, and that they are off. */
  noneAvailable?: boolean;
  onClick: () => void;
}> = ({ doctor, loading, empty, noneAvailable, onClick }) => {
  const src =
    doctor?.profile_picture || doctor?.photo_url || doctor?.image_url;
  const [failed, setFailed] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || empty}
      className="w-full flex items-center gap-2 md:gap-2.5 px-3 py-2 md:px-3.5 md:py-2 rounded-lg border border-doctorsBorder text-left transition-colors hover:border-[#0F3CBE] disabled:opacity-60 disabled:cursor-not-allowed bg-white"
    >
      {doctor ? (
        <>
          {src && !failed ? (
            <img
              src={src}
              alt={doctor.full_name}
              onError={() => setFailed(true)}
              className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover bg-gray-100 flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#0F3CBE] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
              <FiUser className="w-3.5 h-3.5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm md:text-[15px] font-medium text-modalText truncate leading-tight">
              {doctorName(doctor)}
            </p>
            {doctor.specialization && (
              <p className="text-xs md:text-[13px] text-queueText truncate leading-tight mt-0.5">
                {doctor.specialization}
              </p>
            )}
          </div>
        </>
      ) : (
        <span className="flex-1 text-sm md:text-[15px] text-queueText">
          {loading
            ? "Loading doctors…"
            : noneAvailable
            ? "No doctor is available on this date — try another day"
            : empty
            ? "No doctors found"
            : "Choose a doctor"}
        </span>
      )}
      <FiChevronRight className="w-4 h-4 text-queueText flex-shrink-0" />
    </button>
  );
};

// DoctorPickerModal — full doctor list with search. Opens from the trigger
// above. Selecting a doctor sets mappingId and closes the modal.
const DoctorPickerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  doctors: PublicDoctor[];
  selectedMappingId: string | null;
  search: string;
  setSearch: (s: string) => void;
  specializations: string[];
  specialization: string;
  setSpecialization: (s: string) => void;
  /** Doctor-first flow: the day flag is meaningless, everyone is pickable. */
  ignoreAvailability?: boolean;
  onPick: (d: PublicDoctor) => void;
}> = ({ open, onClose, doctors, selectedMappingId, search, setSearch, specializations, specialization, setSpecialization, ignoreAvailability = false, onPick }) => {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sp = specialization.toLowerCase();
    return doctors.filter((d) => {
      const specs = String(d.specialization || "").split(",").map((x) => x.trim().toLowerCase());
      if (sp && !specs.includes(sp)) return false;
      if (!q) return true;
      return d.full_name?.toLowerCase().includes(q) || d.specialization?.toLowerCase().includes(q);
    });
  }, [doctors, search, specialization]);

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={doctorPickerModalStyles}
      ariaHideApp={false}
    >
      <div className="flex flex-col h-full max-h-[85vh]">
        <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
          <p className="text-modalText font-semibold">Choose Doctor</p>
          <button
            type="button"
            onClick={onClose}
            className="text-queueText hover:text-modalText"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-sbBorder">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-doctorsBorder focus-within:border-[#0F3CBE]">
            <FiSearch className="w-4 h-4 text-queueText flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or specialization"
              className="flex-1 min-w-0 text-sm focus:outline-none"
              autoFocus
            />
          </div>
          {specializations.length > 1 && (
            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5">
              <PillChip selected={!specialization} onClick={() => setSpecialization("")} label="All" />
              {specializations.map((sp) => (
                <PillChip key={sp} selected={specialization === sp} onClick={() => setSpecialization(sp)} label={sp} />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {filtered.length === 0 ? (
            <p className="text-sm text-queueText text-center py-8">
              No matching doctors.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {[...filtered]
                .sort((a, b) => Number(ignoreAvailability || (b.available_on_date !== false && b.bookable !== false)) - Number(ignoreAvailability || (a.available_on_date !== false && a.bookable !== false)))
                .map((d) => (
                  <DoctorCard
                    key={d.mapping_id}
                    doctor={d}
                    selected={selectedMappingId === d.mapping_id}
                    available={ignoreAvailability || (d.available_on_date !== false && d.bookable !== false)}
                    onClick={() => onPick(d)}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const OTP_LENGTH = 6;
const OTP_TTL_SECONDS = 300; // 5 min — matches backend
// Backend re-tuned (2026-05-14): per-phone send-otp cooldown is 45s, not
// 60s. Keep this constant in step or the local Resend button will be
// disabled longer than the backend actually requires. The server is the
// source of truth for the per-IP (8/10min) and per-phone (7/10min) caps —
// we don't pre-throttle, just react to the 429 when one is hit.
const RESEND_COOLDOWN_SECONDS = 45;

const formatMmSs = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Mask the phone for display: keep first 2 + last 2 digits, mask the rest.
// e.g. "919876543210" → "+91 98•••••3210"
const maskPhone = (countryCode: string, national: string): string => {
  if (national.length <= 4) return `+${countryCode} ${national}`;
  const first = national.slice(0, 2);
  const last = national.slice(-2);
  const masked = "•".repeat(Math.max(national.length - 4, 0));
  return `+${countryCode} ${first}${masked}${last}`;
};

const OtpModal: React.FC<{
  open: boolean;
  phone: string; // E.164 without +, e.g. "919876543210"
  countryCode: string;
  nationalNumber: string;
  hospitalId: string;
  onClose: () => void;
  onVerified: (bookingToken: string) => void;
  // Invoked when the backend tells us OTP isn't enabled for this hospital
  // (rare — flag fetch should have caught it, but kept for race safety).
  // Parent should close the modal and run the booking flow without a token.
  onSkip: () => void;
  // Invoked when send-otp / verify-otp returns 403 "disabled" — the
  // hospital flipped the master kill-switch mid-session. Parent should
  // close the modal and render the unavailable screen.
  onPublicDisabled: () => void;
  // Pre-formatted (with leading "+") hospital phone — used inline in the
  // OTP daily-ceiling error message so the patient has a fallback path.
  // Empty string when the hospital has no contact_number on file.
  hospitalPhone?: string;
}> = ({
  open,
  phone,
  countryCode,
  nationalNumber,
  hospitalId,
  onClose,
  onVerified,
  onSkip,
  onPublicDisabled,
  hospitalPhone,
}) => {
  const [digits, setDigits] = useState<string[]>(
    () => Array(OTP_LENGTH).fill("")
  );
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Backend collapses lockout-after-5-wrong-attempts into 410 "expired", so
  // the frontend doesn't track per-attempt counters. The only session-level
  // lock we track here is the send-otp 10-minute hard cap (3 sends per
  // phone/IP per 10 min); when tripped, Resend is permanently disabled for
  // the rest of the modal session.
  const [hardCapped, setHardCapped] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(OTP_TTL_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [expired, setExpired] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const otp = digits.join("");

  const resetState = () => {
    setDigits(Array(OTP_LENGTH).fill(""));
    setError(null);
    setHardCapped(false);
    setOtpSecondsLeft(OTP_TTL_SECONDS);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setExpired(false);
  };

  const doSend = async () => {
    if (!phone || !hospitalId) return;
    setSending(true);
    setError(null);
    try {
      await sendBookingOtp({ contact_number: phone, hospital_id: hospitalId });
      // Restart per-OTP timers but don't touch hardCapped — once tripped,
      // it stays for the rest of the modal session.
      setDigits(Array(OTP_LENGTH).fill(""));
      setOtpSecondsLeft(OTP_TTL_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setExpired(false);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    } catch (err: any) {
      const status = err?.response?.status;
      const rawError = err?.response?.data?.error;
      const errMsg = typeof rawError === "string" ? rawError : "";
      if (status === 403 && /disabled/i.test(errMsg)) {
        // Hospital flipped the kill-switch between widget load and OTP open.
        // Bubble up — parent closes the modal and renders the unavailable
        // screen.
        onPublicDisabled();
        return;
      } else if (status === 429) {
        // Backend overloads 429 with three distinct meanings — disambiguate
        // by parsing the error message:
        //   - "Daily OTP send limit reached" → hospital-level OTP credit
        //     ceiling tripped → permanent session lock + a clear message
        //     pointing the patient at the hospital phone.
        //   - "Please try again in 10 minutes" → hard cap (server-side
        //     send rate-limit per phone/IP per 10 min) → permanent
        //     session lock.
        //   - other → per-phone send cooldown (45s) → drive the local
        //     cooldown from Retry-After when present.
        if (/daily/i.test(errMsg) && /otp/i.test(errMsg)) {
          setHardCapped(true);
          setError(
            hospitalPhone
              ? `Today's OTP limit reached. Please call the hospital at ${hospitalPhone}.`
              : "Today's OTP limit reached. Please call the hospital."
          );
        } else if (/10\s*minutes?/i.test(errMsg)) {
          setHardCapped(true);
          setError(
            "You've requested too many codes — please wait 10 minutes."
          );
        } else {
          const retryAfterRaw =
            err?.response?.headers?.["retry-after"] ??
            err?.response?.data?.result?.retry_after;
          const retryAfter = Number(retryAfterRaw);
          setResendCooldown(
            Number.isFinite(retryAfter) && retryAfter > 0
              ? Math.ceil(retryAfter)
              : RESEND_COOLDOWN_SECONDS
          );
          setError("Please wait a moment before requesting another code.");
        }
      } else if (status === 400) {
        // Defensive fallback: feature-flag fetch could race with a backend
        // toggle. If the server says OTP isn't enabled for this hospital,
        // the spec is to skip OTP entirely and let the booking flow
        // proceed without a token rather than block the patient.
        if (/not enabled/i.test(errMsg)) {
          onSkip();
          return;
        }
        setError("Couldn't send the code. Please check your phone number.");
      } else {
        // 502 / 500 / network — never echo backend strings, they leak
        // implementation detail ("OTP storage unavailable" etc.). Match
        // the canonical spec wording so the message is predictable.
        setError("Could not send the OTP right now. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  // Fire send-otp once when the modal opens.
  useEffect(() => {
    if (!open) return;
    resetState();
    doSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // OTP TTL countdown.
  useEffect(() => {
    if (!open || expired) return;
    if (otpSecondsLeft <= 0) {
      setExpired(true);
      return;
    }
    const t = setTimeout(() => setOtpSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [open, otpSecondsLeft, expired]);

  // Resend cooldown.
  useEffect(() => {
    if (!open || resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [open, resendCooldown]);

  const handleDigitChange = (idx: number, value: string) => {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      const next = [...digits];
      next[idx] = "";
      setDigits(next);
      return;
    }
    // Support paste: if more than 1 char comes in, distribute across boxes.
    if (clean.length > 1) {
      const next = [...digits];
      for (let i = 0; i < clean.length && idx + i < OTP_LENGTH; i++) {
        next[idx + i] = clean[i];
      }
      setDigits(next);
      const lastFilled = Math.min(idx + clean.length, OTP_LENGTH) - 1;
      inputsRef.current[lastFilled]?.focus();
      return;
    }
    const next = [...digits];
    next[idx] = clean[0];
    setDigits(next);
    if (idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handleDigitKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1)
      inputsRef.current[idx + 1]?.focus();
  };

  const doVerify = async () => {
    if (otp.length !== OTP_LENGTH) {
      setError("Enter the 6-digit code.");
      return;
    }
    if (expired) {
      setError("Code expired. Tap Resend.");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const res = await verifyBookingOtp({
        contact_number: phone,
        hospital_id: hospitalId,
        otp,
      });
      onVerified(res.booking_token);
    } catch (err: any) {
      const status = err?.response?.status;
      const rawError = err?.response?.data?.error;
      const errMsg = typeof rawError === "string" ? rawError : "";
      if (status === 403 && /disabled/i.test(errMsg)) {
        // Same race as in doSend — kill-switch flipped after the OTP was
        // sent but before verify completed. Bubble up to the parent.
        onPublicDisabled();
        return;
      }
      if (status === 410) {
        // 410 covers BOTH true TTL expiry AND post-lockout (5+ wrong tries),
        // collapsed by the backend to avoid leaking the lockout signal. The
        // canonical spec says: toast + re-trigger send-otp flow. So we
        // surface a toast and auto-fire a fresh send. If the modal is
        // already hard-capped (server's per-IP/per-phone send cap was
        // tripped earlier), fall back to inline messaging instead.
        if (hardCapped) {
          setExpired(true);
          setError("Code expired. Please close and try again later.");
        } else {
          toast.error("Code expired — sending a new one.");
          doSend();
        }
      } else if (status === 401) {
        setDigits(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        setError("Wrong code. Try again.");
      } else if (status === 429) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("We couldn't verify the code. Please try again.");
      }
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit when all 6 digits are typed.
  useEffect(() => {
    if (otp.length === OTP_LENGTH && !verifying && !expired && !hardCapped) {
      doVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      style={doctorPickerModalStyles}
      ariaHideApp={false}
    >
      <div className="flex flex-col max-h-[85vh]">
        <div className="px-5 lg:px-6 py-4 flex items-center justify-between border-b border-sbBorder">
          <p className="text-modalText font-semibold text-base lg:text-lg">
            Verify your phone
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-queueText hover:text-modalText"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 lg:p-6 space-y-5">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E7F8EF] text-[#1FAD63] text-xs font-semibold mb-2">
              <FaWhatsapp className="w-3.5 h-3.5" />
              Sent on WhatsApp
            </div>
            <p className="text-sm text-queueText">
              We sent a 6-digit code on WhatsApp to
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-modalText font-semibold text-base">
                {maskPhone(countryCode, nationalNumber)}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="text-[#0F3CBE] text-xs font-medium underline-offset-2 hover:underline"
              >
                Edit
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm text-modalText text-center mb-3">
              Enter the code below to confirm your appointment booking.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-center">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  disabled={sending || verifying || expired || hardCapped}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg lg:text-xl font-semibold border border-doctorsBorder rounded-lg focus:outline-none focus:border-[#0F3CBE] disabled:bg-gray-50 disabled:text-queueText"
                />
              ))}
            </div>

            {error && (
              <p className="mt-3 text-sm text-onLeave text-center">
                {error}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between text-xs text-queueText">
              <span>
                {expired ? (
                  <span className="text-onLeave font-medium">Code expired</span>
                ) : (
                  <>Code expires in {formatMmSs(Math.max(otpSecondsLeft, 0))}</>
                )}
              </span>
              <button
                type="button"
                disabled={sending || resendCooldown > 0 || hardCapped}
                onClick={doSend}
                className="text-[#0F3CBE] font-medium disabled:text-queueText disabled:cursor-not-allowed"
              >
                {sending
                  ? "Sending…"
                  : hardCapped
                  ? "Try later"
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend OTP"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={doVerify}
            disabled={
              verifying ||
              sending ||
              otp.length !== OTP_LENGTH ||
              expired ||
              hardCapped
            }
            className="w-full px-5 py-3 lg:py-3.5 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm lg:text-base hover:bg-[#0a2f99] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {verifying ? "Verifying…" : "Verify & Book"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// What the "i" reveals about a doctor: everything the public endpoint knows.
const doctorFacts = (d: PublicDoctor): Array<[string, string]> => {
  const facts: Array<[string, string]> = [];
  if (d.specialization) facts.push(["Specialization", String(d.specialization)]);
  if (d.qualification) facts.push(["Qualification", String(d.qualification)]);
  const yrs = Number(d.experience_years ?? d.experience);
  if (Number.isFinite(yrs) && yrs > 0) facts.push(["Experience", `${yrs} ${yrs === 1 ? "year" : "years"}`]);
  const fee = Number(d.physical_amount ?? d.consultation_fee);
  if (Number.isFinite(fee) && fee > 0) facts.push(["Consultation fee", `₹${fee}`]);
  if (d.gender) facts.push(["Gender", String(d.gender)]);
  return facts;
};

const DoctorDetails: React.FC<{ doctor: PublicDoctor; className?: string }> = ({ doctor, className }) => {
  const facts = doctorFacts(doctor);
  return (
    <div className={`rounded-lg border border-doctorsBorder bg-[#FAFBFF] px-3 py-2 ${className || ""}`}>
      {facts.length === 0 ? (
        <p className="text-[12px] text-queueText">No further details on file.</p>
      ) : (
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
          {facts.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-[11px] uppercase tracking-wide text-queueText pt-0.5">{k}</dt>
              <dd className="text-[13px] text-modalText break-words">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
};

// "By specialization" popup: the list of specializations, then the doctors
// under the chosen one (available first; off-that-day rows greyed).
const SpecializationPickerModal: React.FC<{
  open: boolean;
  onClose: () => void;
  specializations: string[];
  doctors: PublicDoctor[];
  selectedMappingId: string | null;
  initialSpecialization: string;
  isAvailable: (d: PublicDoctor) => boolean;
  onPick: (specialization: string, d: PublicDoctor) => void;
}> = ({ open, onClose, specializations, doctors, selectedMappingId, initialSpecialization, isAvailable, onPick }) => {
  const [chosen, setChosen] = useState<string>("");
  useEffect(() => {
    if (open) setChosen(initialSpecialization);
  }, [open, initialSpecialization]);
  const under = (sp: string) =>
    doctors.filter((d) =>
      String(d.specialization || "")
        .split(",")
        .map((x) => x.trim().toLowerCase())
        .includes(sp.toLowerCase())
    );
  const list = chosen ? [...under(chosen)].sort((a, b) => Number(isAvailable(b)) - Number(isAvailable(a))) : [];
  return (
    <Modal isOpen={open} onRequestClose={onClose} style={doctorPickerModalStyles} ariaHideApp={false}>
      <div className="flex flex-col h-full max-h-[85vh]">
        <div className="px-5 py-3 flex items-center justify-between border-b border-sbBorder">
          <div className="flex items-center gap-2 min-w-0">
            {chosen && (
              <button type="button" onClick={() => setChosen("")} className="text-xs font-semibold text-[#0F3CBE]">
                ← All
              </button>
            )}
            <p className="text-modalText font-semibold truncate">{chosen || "Choose a specialization"}</p>
          </div>
          <button type="button" onClick={onClose} className="text-queueText hover:text-modalText" aria-label="Close">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {!chosen ? (
            <div className="flex flex-col gap-1.5">
              {specializations.map((sp) => {
                const n = under(sp).length;
                const avail = under(sp).filter(isAvailable).length;
                return (
                  <button
                    key={sp}
                    type="button"
                    onClick={() => setChosen(sp)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-doctorsBorder bg-white text-left hover:border-[#0F3CBE]"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-modalText truncate">{sp}</span>
                      <span className="block text-xs text-queueText">
                        {n} {n === 1 ? "doctor" : "doctors"}
                        {avail !== n ? ` · ${avail} available` : ""}
                      </span>
                    </span>
                    <FiChevronRight className="w-4 h-4 text-queueText flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          ) : list.length === 0 ? (
            <p className="text-sm text-queueText text-center py-8">No doctors under {chosen}.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {list.map((d) => (
                <DoctorCard
                  key={d.mapping_id}
                  doctor={d}
                  selected={selectedMappingId === d.mapping_id}
                  available={isAvailable(d)}
                  onClick={() => onPick(chosen, d)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// Floating details panel anchored to an "i" button. Closes on outside click
// or Escape. Takes no layout space, unlike an inline panel.
const InfoPopover: React.FC<{
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  label: string;
  buttonClassName: string;
  children: React.ReactNode;
}> = ({ open, onToggle, onClose, label, buttonClassName, children }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return (
    <div ref={ref} className="relative shrink-0 flex">
      <button type="button" onClick={onToggle} aria-label={label} aria-expanded={open} className={buttonClassName}>
        <FiInfo className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 w-[min(18rem,80vw)] rounded-lg border border-doctorsBorder bg-white shadow-[0_12px_32px_-12px_rgba(15,60,190,0.35)]">
          {children}
        </div>
      )}
    </div>
  );
};

const DoctorCard: React.FC<{
  doctor: PublicDoctor;
  selected: boolean;
  available?: boolean;
  onClick: () => void;
}> = ({ doctor, selected, available = true, onClick }) => {
  const src = doctor.profile_picture || doctor.photo_url || doctor.image_url;
  const [failed, setFailed] = useState(false);
  const [info, setInfo] = useState(false);
  return (
    <div
      className={[
        "rounded-lg border transition-colors",
        !available
          ? "border-doctorsBorder bg-[#F7F7F9]"
          : selected
            ? "border-[#0F3CBE] bg-[#EAF0FF]"
            : "border-doctorsBorder hover:border-[#0F3CBE]",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <button
          type="button"
          onClick={onClick}
          disabled={!available}
          aria-disabled={!available}
          className={`flex items-center gap-2 flex-1 min-w-0 text-left ${available ? "" : "opacity-60 cursor-not-allowed"}`}
        >
          {src && !failed ? (
            <img
              src={src}
              alt={doctor.full_name}
              onError={() => setFailed(true)}
              className="w-8 h-8 rounded-full object-cover bg-gray-100 flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#0F3CBE] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
              <FiUser className="w-3.5 h-3.5" />
            </div>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-modalText truncate leading-tight">{doctorName(doctor)}</span>
            <span className="block text-xs truncate leading-tight">
              {!available ? (
                <span className="text-[#B45309] font-medium">Not available on this day</span>
              ) : (
                <span className="text-queueText">{doctor.specialization}</span>
              )}
              {!available && doctor.specialization && <span className="text-queueText"> · {doctor.specialization}</span>}
            </span>
          </span>
        </button>
        <InfoPopover
          open={info}
          onToggle={() => setInfo((v) => !v)}
          onClose={() => setInfo(false)}
          label={`About ${doctorName(doctor)}`}
          buttonClassName={`w-8 h-8 rounded-full flex items-center justify-center ${
            info ? "text-[#0F3CBE] bg-white" : "text-queueText hover:text-[#0F3CBE]"
          }`}
        >
          <DoctorDetails doctor={doctor} />
        </InfoPopover>
      </div>
    </div>
  );
};

const formatTime = (t: string | undefined): string => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return t;
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m ?? "00"} ${suffix}`;
};

// Compact 12-hour format used in the booking_session_start/end_time payload
// fields. Admin sends "12:00PM" / "01:00PM" — zero-padded hour, no space
// before AM/PM. Different from formatTime above (which has the space and is
// for user-facing labels).
const formatTime12Compact = (t: string | undefined): string => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return t;
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, "0")}:${(m ?? "00").slice(0, 2)}${suffix}`;
};

const CenteredCard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex items-center justify-center px-4 py-12 min-h-[60vh]">
    <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
      {children}
    </div>
  </div>
);

// True when an axios error matches the backend's master kill-switch 403.
// Centralised so every server-call catch path can surface the unavailable
// screen consistently — if the hospital flips the switch mid-session, even
// a stray read endpoint should bubble it up instead of degrading silently.
const isKillSwitchError = (err: any): boolean => {
  if (err?.response?.status !== 403) return false;
  const raw = err?.response?.data?.error;
  return typeof raw === "string" && /disabled/i.test(raw);
};

// Pretty-prints a raw contact_number for use in toast strings. The DB column
// holds the digits-only form (e.g. "919876543210"); we add a "+" if missing
// so it reads like a phone number rather than an opaque token. Returns ""
// when there's nothing usable so callers can branch.
const formatHospitalPhone = (raw: string | undefined | null): string => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
};

// Master kill-switch screen — shown when public_booking_enabled is false
// at widget load OR when any later call returns 403 "disabled". No form,
// no OTP, no retry; the only action is to call the hospital.
const UnavailableScreen: React.FC<{ branding: HospitalBranding }> = ({
  branding,
}) => {
  const phoneRaw = branding.contact_number || branding.contact_info?.phone;
  const phone = formatHospitalPhone(phoneRaw);
  const tel = phone ? phone.replace(/\s+/g, "") : "";
  return (
    <div className="min-h-screen bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl border border-doctorsBorder shadow-[0_8px_32px_-16px_rgba(15,60,190,0.2)] overflow-hidden">
        <div className="h-0.5 md:h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
        <div className="p-6 md:p-8 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-modalText mb-2 leading-tight">
            Online booking is temporarily unavailable
          </h2>
          <p className="text-sm md:text-[15px] text-queueText mb-6">
            {branding.hospital_name
              ? `${branding.hospital_name} isn't accepting online appointments right now. `
              : "This hospital isn't accepting online appointments right now. "}
            Please call the hospital directly or visit in person.
          </p>
          {tel ? (
            <a
              href={`tel:${tel}`}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm md:text-[15px] hover:bg-[#0a2f99] transition-colors shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
            >
              Call {phone}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Shown in place of the form once a booking is created (or a 409 replay says
// it already exists). The primary action returns the patient to the hospital's
// WhatsApp chat, where the confirmation message lands.
const ConfirmedScreen: React.FC<{
  branding: HospitalBranding;
  booking: ConfirmedBooking;
  // Came in through the WhatsApp link: another booking starts with "Hi" in the
  // chat (a fresh link), not with this page.
  fromWhatsApp?: boolean;
  onBookAnother: () => void;
}> = ({ branding, booking, fromWhatsApp, onBookAnother }) => {
  const waDigits = String(
    branding.contact_number || branding.contact_info?.phone || ""
  ).replace(/\D/g, "");
  const shortRef = booking.ref
    ? booking.ref.replace(/-/g, "").slice(0, 8).toUpperCase()
    : "";
  const firstName = booking.patient.split(" ")[0];
  // Held until paid: the fee comes first, the WhatsApp confirmation after it.
  // The patient often pays on another screen (the SMS / WhatsApp link, or the
  // Razorpay tab), so while pending re-check the booking every 5 s and when
  // this tab comes back into view; paid -> the screen turns into "confirmed".
  const [payState, setPayState] = useState<"pending" | "paid" | "expired">("pending");
  useEffect(() => {
    if (!booking.payment || !booking.ref || payState !== "pending") return;
    let token: string | null = null;
    try {
      token = window.sessionStorage.getItem(bookingPageTokenKey(booking.ref));
    } catch {
      token = null;
    }
    if (!token) return;
    let stopped = false;
    const check = () => {
      if (stopped || document.visibilityState === "hidden") return;
      getPublicBookingDetail(booking.ref as string, branding.hospital_id, token as string)
        .then((d) => {
          if (stopped) return;
          const st = d.payment?.state;
          if (st === "paid" || st === "not_required") setPayState("paid");
          else if (st === "expired") setPayState("expired");
        })
        .catch(() => undefined);
    };
    const id = window.setInterval(check, 5000);
    document.addEventListener("visibilitychange", check);
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", check);
    };
  }, [booking.payment, booking.ref, branding.hospital_id, payState]);
  const pay = payState === "pending" ? booking.payment || null : null;
  const payExpired = payState === "expired";
  const payAmount = pay && pay.amount != null ? (pay.currency && pay.currency !== "INR" ? `${pay.currency} ${pay.amount}` : `₹${pay.amount}`) : "";
  const managePath = booking.manageable && booking.ref ? publicHref(`/${branding.hospital_id}/manage/${booking.ref}`) : "";
  const rows: [string, string][] = [
    ["Patient", booking.patient],
    ["Phone", booking.phone],
    ["Doctor", booking.doctor],
    ["When", booking.when],
    ["Visit", booking.visit],
  ];
  return (
    <div className="min-h-screen bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE] flex items-center justify-center px-4 py-8">
      <style>{`
        @keyframes dtPop { 0% { transform: scale(.6); opacity: 0 } 70% { transform: scale(1.08); opacity: 1 } 100% { transform: scale(1) } }
        @keyframes dtRise { from { transform: translateY(10px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
      <div
        className="max-w-md w-full bg-white rounded-2xl border border-doctorsBorder shadow-[0_8px_32px_-16px_rgba(15,60,190,0.2)] overflow-hidden"
        style={{ animation: "dtRise .35s ease-out both" }}
      >
        <div className="h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
        <div className="px-6 pt-7 pb-5 text-center">
          <div
            className={`mx-auto w-16 h-16 rounded-full text-white flex items-center justify-center ${
              pay || payExpired
                ? "bg-gradient-to-br from-[#F59E0B] to-[#D97706] shadow-[0_12px_28px_-12px_rgba(217,119,6,0.7)]"
                : "bg-gradient-to-br from-[#1FAD63] to-[#17A05A] shadow-[0_12px_28px_-12px_rgba(31,173,99,0.7)]"
            }`}
            style={{ animation: "dtPop .45s cubic-bezier(.2,.9,.3,1.3) both" }}
          >
            {pay || payExpired ? <FiCreditCard className="w-7 h-7" /> : <FiCheck className="w-8 h-8" strokeWidth={3} />}
          </div>
          <p className={`mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] ${pay || payExpired ? "text-[#B45309]" : "text-[#0F3CBE]"}`}>
            {pay ? "Payment pending" : payExpired ? "Payment time over" : booking.rescheduled ? "Appointment rescheduled" : "Booking confirmed"}
          </p>
          <h2 className="mt-1 text-2xl font-bold text-modalText leading-tight">
            {pay
              ? `One step left${firstName ? `, ${firstName}` : ""}`
              : payExpired
                ? "This booking was released"
                : `See you soon${firstName ? `, ${firstName}` : ""}!`}
          </h2>
          <p className="mt-1 text-sm text-queueText">
            {pay
              ? `Pay ${payAmount || "the booking fee"} to confirm your appointment with ${branding.hospital_name}.${
                  pay.expires_at ? ` Your slot is held until ${moment(pay.expires_at).format("h:mm A")}.` : ""
                }`
              : payExpired
                ? "The fee wasn't paid in time, so the slot was released. Send Hi on WhatsApp to book again."
                : booking.rescheduled
                ? `Your appointment with ${branding.hospital_name} has moved to the new time.`
                : `Your appointment with ${branding.hospital_name} is booked.`}
          </p>
          {shortRef && (
            <p className="mt-2 inline-block px-2.5 py-1 rounded-full bg-[#EAF0FF] text-[#0F3CBE] text-xs font-semibold tracking-wide">
              Ref {shortRef}
            </p>
          )}
        </div>
        <div className="mx-6 rounded-xl border border-doctorsBorder divide-y divide-doctorsBorder bg-[#FAFBFF]">
          {rows
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-4 px-3.5 py-2.5">
                <p className="text-xs text-queueText pt-0.5">{k}</p>
                <p className="text-sm font-medium text-modalText text-right break-words">{v}</p>
              </div>
            ))}
        </div>
        <div className="px-6 pt-5 pb-6">
          {pay ? (
            pay.link ? (
              <a
                href={pay.link}
                className="mb-3 flex items-center justify-center gap-2 w-full px-5 py-3 rounded-lg bg-[#0F3CBE] text-white font-semibold text-sm hover:bg-[#0a2f99] transition-colors shadow-[0_8px_24px_-12px_rgba(15,60,190,0.5)]"
              >
                <FiCreditCard className="w-4 h-4" />
                Pay {payAmount}
              </a>
            ) : (
              <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 text-center">
                The payment link couldn't be created. Please contact the hospital to confirm this booking.
              </p>
            )
          ) : null}
          <p className="mb-4 flex items-center justify-center gap-1.5 text-xs text-queueText text-center">
            <FaWhatsapp className="w-3.5 h-3.5 text-[#1FAD63] flex-shrink-0" />
            {pay ? "Your confirmation comes on WhatsApp once you've paid." : "Your confirmation is on its way to your WhatsApp."}
          </p>
          {managePath && (
            <a
              href={managePath}
              className="mb-3 flex items-center justify-center w-full px-5 py-2.5 rounded-lg border border-doctorsBorder text-modalText bg-white font-semibold text-sm hover:border-[#0F3CBE] transition-colors"
            >
              {booking.rescheduled ? "Back to my booking" : "View my booking"}
            </a>
          )}
          {waDigits ? (
            <a
              href={`https://wa.me/${waDigits}`}
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-lg bg-[#1FAD63] text-white font-semibold text-sm hover:bg-[#17A05A] transition-colors shadow-[0_8px_24px_-12px_rgba(31,173,99,0.6)]"
            >
              <FaWhatsapp className="w-4 h-4" />
              Back to WhatsApp
            </a>
          ) : null}
          {fromWhatsApp ? (
            <p className="mt-4 text-center text-sm text-queueText">
              Want to book another appointment? Send <span className="font-semibold text-modalText">Hi</span> on WhatsApp.
            </p>
          ) : (
            <button
              type="button"
              onClick={onBookAnother}
              className="mt-3 w-full px-5 py-2.5 rounded-lg border border-[#0F3CBE] text-[#0F3CBE] bg-white font-semibold text-sm hover:bg-[#EAF0FF] transition-colors"
            >
              {booking.rescheduled ? "Book a new appointment" : "Book another appointment"}
            </button>
          )}
          <p className="mt-5 text-center text-[11px] text-queueText">
            Powered by{" "}
            <span className="font-bold bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0] bg-clip-text text-transparent">
              DocTrue
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

// WhatsApp message languages, in their own script (same names as the bot's
// language list, LANGUAGE_INTRO in the backend).
const MESSAGE_LANG_NAMES: Record<string, string> = {
  en: "English",
  hi: "हिंदी",
  kn: "ಕನ್ನಡ",
  ta: "தமிழ்",
  te: "తెలుగు",
  ml: "മലയാളം",
  mr: "मराठी",
  bn: "বাংলা",
  ar: "العربية",
};

// Standalone hospital identity strip rendered above the two form cards.
// Mirrors admin AppointmentBooking's page-level header treatment, with
// an extra "Secure online booking" trust badge that's specific to the
// public widget (the patient hasn't logged in, so a trust signal helps).
const CardHeader: React.FC<{ branding: HospitalBranding }> = ({ branding }) => {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const initial = branding.hospital_name?.charAt(0)?.toUpperCase() || "H";
  return (
    <div className="flex items-center justify-between gap-4 mt-2 px-4 sm:px-5 py-2.5 md:py-3 rounded-xl bg-gradient-to-r from-white to-[#F8FAFF] border border-doctorsBorder md:shadow-[0_4px_20px_-12px_rgba(15,60,190,0.18)]">
      <div className="flex items-center gap-3 min-w-0">
        {branding.logo_url && !logoFailed ? (
          <img
            src={branding.logo_url}
            alt={branding.hospital_name}
            onError={() => setLogoFailed(true)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-[#0F3CBE] to-[#3D5AE0] text-white font-bold text-base md:text-lg flex items-center justify-center flex-shrink-0">
            {initial}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-modalText font-semibold text-sm md:text-base truncate leading-tight">
            {branding.hospital_name}
          </p>
          {(branding.address || branding.city) && (
            <p className="text-[11px] md:text-xs text-queueText truncate leading-tight mt-0.5">
              {[branding.address, branding.city].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EAF0FF] text-[#0F3CBE] text-[11px] md:text-xs font-semibold whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0F3CBE]" />
        Secure online booking
      </div>
    </div>
  );
};

// On md+ we make this header more compact and visually refined so the
// rest of the form has room to fit in a single viewport: smaller h1,
// tighter margins, and a brand-gradient text fill on "appointment" to
// give the heading a finished feel without bloating its size.
const PageTitle: React.FC<{ hospitalName?: string; reschedule?: boolean }> = ({ hospitalName, reschedule }) => (
  <div className="mb-4 md:mb-3 lg:mb-4">
    <div className="flex items-center gap-2 mb-1.5">
      <span
        className="h-1 w-8 lg:w-10 rounded-full bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0]"
        aria-hidden
      />
      <span className="text-[11px] lg:text-xs font-semibold uppercase tracking-[0.18em] text-[#0F3CBE]">
        {reschedule ? "Reschedule" : "Online booking"}
      </span>
    </div>
    <h1 className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold text-modalText leading-tight tracking-tight">
      {reschedule ? "Move your" : "Book your"}{" "}
      <span className="bg-gradient-to-r from-[#0F3CBE] to-[#3D5AE0] bg-clip-text text-transparent">
        appointment
      </span>
      {hospitalName ? (
        <span className="block text-sm sm:text-base md:text-sm lg:text-base font-medium text-queueText mt-1">
          with {hospitalName}
        </span>
      ) : null}
    </h1>
  </div>
);

export default PublicBooking;
